import { queryOptions } from '@tanstack/react-query'
import { ApiError, apiExchange, apiGet, apiPost, apiPostForm, encodePost, sendToLogin, type PostValue } from './client'
import { saveBytes } from './inventoree'

/**
 * Contractul lui /api/v2/devices/{id}/workspace - carcasa fisei unei masini
 * (perechea lui inventoree/getActionTabs + deviceOverview din portal).
 */

export type Badge =
  | { kind: 'label'; count: number }
  | { kind: 'attached'; count: number; problems: number }
  | { kind: 'startup_apps' | 'startup_services'; count: number; stopped: number }

export interface WorkspaceItem {
  label: string
  /** Adresa din portal, fara numarul masinii: `inventoree/deviceSummary/`. E si cheia ecranului. */
  href: string
  after: string
  review: string | null
  badge: Badge | null
}

export interface WorkspaceModule {
  slug: string
  name: string
  icon: string
  color: string
  open: boolean
  items: WorkspaceItem[]
}

export interface OverviewModule {
  slug: string
  name: string
  icon: string
  color: string
  summary: string
}

export interface HealthCell {
  href: string
  link: boolean
  value: string
  label: string
  tone: string
  title: string
}

export interface Workspace {
  device: {
    id: number
    name: string
    host: string
    type: number
    lastSeen: string
    lastSeenAge: number | null
    lastSeenLabel: string
  }
  current: string
  modules: WorkspaceModule[]
  overview: OverviewModule[]
  health: { silent: boolean; cells: HealthCell[] } | null
}

export function workspaceQuery(id: number, from: string) {
  return queryOptions({
    queryKey: ['device', id, 'workspace', from],
    queryFn: () => apiGet<Workspace>(`/devices/${id}/workspace?from=${encodeURIComponent(from)}`),
    staleTime: 30_000,
    /* "Reported" se improspateaza singur (cec-master ee7c118ff): la un minut,
       si numai cat fila e la vedere - cu fila ascunsa nu se cere nimic. Eticheta
       de langa Overview si celula din banda pornesc din nou de la varsta
       adusa de server, deci un aparat care a raportat intre timp isi schimba
       culoarea fara refresh. */
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Summary - /api/v2/devices/{id}/summary (Inventoree::deviceSummary +
 * summary.php) si scrierile care pleaca de pe ea.
 * ------------------------------------------------------------------------- */

export interface SummaryTile {
  number: string
  icon: string
  label: string
  title: string
  /** HTML deja scapat (poate contine &middot;). */
  note: string
  more: { href: string; label: string }
}

/**
 * Sugestia de stapan din logarile AD (`OwnerMarks::suggestionView`): cine a
 * stat pe masina in cele mai multe zile, sub pragul de asignare automata.
 * `canAccept` = omul are dreptul de alocare (fara el, butoanele nu apar).
 */
export interface OwnerSuggestion {
  name: string
  account: string
  email: string
  days: number
  window: number
  /** "Ion Popescu - logged in on 4 of the last 30 days" */
  text: string
  canAccept: boolean
}

/** Stapanul pus AUTOMAT din AD (insigna "Auto"); `title` e textul de la server. */
export interface OwnerAuto {
  title: string
  how: string
}

export type SuggestionAnswer = { status: 'OK' | 'ERROR'; message: string; owner?: { id: number; name: string } }

/** [Assign] pe o sugestie: asignare de MANA, marcata ca venita din sugestie. */
export function acceptOwnerSuggestion(id: number) {
  return apiPost<SuggestionAnswer>(`/devices/${id}/owner/suggestion/accept`, {})
}

/** [Dismiss]: contul nu mai e propus (nici pus automat) pe aparatul asta. */
export function dismissOwnerSuggestion(id: number) {
  return apiPost<SuggestionAnswer>(`/devices/${id}/owner/suggestion/dismiss`, {})
}

export interface OwnerOption {
  id: number
  name: string
  email: string
}

export interface SummaryFact {
  label: string
  icon: string
  /** HTML deja scapat. */
  text: string
  links?: { id: number; name: string }[]
}

export interface SummaryDocument {
  id: number
  name: string
  type: string
  size: string
  uploaded: string
  download: string
}

export interface DeviceSummaryPage {
  device: { id: number; label: string; host: string; name: string; alias: string; type: number }
  tiles: SummaryTile[]
  /** Class -> Type -> Subtype -> Role -> Brand -> Model -> OS -> Version (Inventoree::taxonomySummaryPath). */
  taxonomyPath?: string[]
  owner: {
    id: number
    name: string
    can_assign: boolean
    options: OwnerOption[]
    /** Lipsa (server mai vechi) sau null = pus de mana / fara stapan. */
    auto?: OwnerAuto | null
    suggestion?: OwnerSuggestion | null
  }
  rename: { ready: boolean; alias: string; host: string; family: string; warning: string; max: number }
  facts: SummaryFact[]
  qr: { src: string; width: number } | null
  picture: { src: string } | null
  documents: SummaryDocument[]
}

export function summaryQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'summary'],
    queryFn: () => apiGet<DeviceSummaryPage>(`/devices/${id}/summary`),
    /* Fila se cere din nou la fiecare deschidere, ca fragmentul adus prin ajax,
       si nu se reface pe la spate cat e deschisa (ar inchide alegerea proprietarului). */
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function removeDocument(id: number, doc: number) {
  return apiPost<{ removed: boolean }>(`/devices/${id}/documents/${doc}/remove`, {})
}

/** Overview::massAssign cu o masina: `device_owner` 0 inseamna Nobody. */
export function assignOwner(id: number, owner: number) {
  return apiPost<{ changed: number }>(`/devices/${id}/owner`, { device_owner: owner })
}

export function quickAddOwner(id: number, firstname: string, lastname: string, email: string) {
  return apiPost<{ id: number; text: string }>(`/devices/${id}/owner/quick-add`, { firstname, lastname, email })
}

export type AliasResult = { status: 'OK'; alias: string; host: string; label: string } | { status: 'ERROR'; message: string }

export function saveAlias(id: number, alias: string) {
  return apiPost<AliasResult>(`/devices/${id}/alias`, { alias })
}

export type RenameResult = { status: 'OK'; draft: number; link: string; message: string } | { status: 'ERROR'; message: string }

export function proposeRename(id: number, name: string) {
  return apiPost<RenameResult>(`/devices/${id}/rename-request`, { name })
}

/* ---------------------------------------------------------------------------
 * Fila Softsee Summary - /api/v2/devices/{id}/softsee-summary
 * (Softsee::deviceSummary + deviceSummary.php)
 * ------------------------------------------------------------------------- */

export interface SoftseeSummaryTile {
  number: string
  icon: string
  label: string
  title: string
  note: string
  tone: string
  text: boolean
  more: { href: string; label: string }
}

export interface SoftseeApplications {
  operating_system: string
  office: string
  default_mail_client: string
  default_browser: string
  antivirus: string
  firewall: string
  antispyware: string
  os_available_updates: number
}

export interface SoftseeOutdatedApp {
  application_name: string
  version: string
  last_version: string
}

export interface DeviceSoftseeSummaryPage {
  device: { id: number }
  tiles: SoftseeSummaryTile[]
  applications: SoftseeApplications | null
  outdated: SoftseeOutdatedApp[]
}

export function softseeSummaryQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'softsee-summary'],
    queryFn: () => apiGet<DeviceSoftseeSummaryPage>(`/devices/${id}/softsee-summary`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ------------------------------------------------------------------------- */
/* Fila Opteemum Summary - /api/v2/devices/{id}/opteemum-summary             */
/* ------------------------------------------------------------------------- */

export interface OpteemumSummaryTile {
  number: string
  icon: string
  label: string
  more: string
  tone: string
  font24: boolean
}

export interface OpteemumOsApps {
  operating_system: string
  office: string
  default_mail_client: string
  default_browser: string
  antivirus: string
  antivirus_mark: string
  firewall: string
  firewall_mark: string
  antispyware: string
  os_available_updates: string
  is_server: boolean
  linux_guard: boolean
}

export interface OpteemumTuneup {
  last_os: string
  last_browsing: string
  last_tuneup: string
  last_privacy: string
  current_boot: string
  current_boot_note: string
  previous_boot: string
  best_boot: string
}

export interface OpteemumPrimary {
  service: string
  version: string
  package: string
  signals: { label: string; display: string }[]
}

export interface OpteemumServicePanel {
  role_label: string
  others: string[]
  next_text: string
  next_cls: string
  reasons: string[]
  updates_count: number
  updates_security: boolean
  updates_name: string
  updates_version: string
  updates_cves: string[]
  updates_detail: string
  optimizations: { title: string; detail: string; cls: string }[]
  config_score: string
  config_score_badge: string
  config_items: { path: string; status: string; note: string; badge: string }[]
}

export interface DeviceOpteemumSummaryPage {
  device: { id: number }
  is_server: boolean
  tiles: OpteemumSummaryTile[]
  os_apps: OpteemumOsApps | null
  health_rows: { processor: string; memory: string; storage: string; cpu_temp: string; storage_temp: string }
  tuneup: OpteemumTuneup | null
  primary: OpteemumPrimary | null
  service: OpteemumServicePanel | null
}

export function opteemumSummaryQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'opteemum-summary'],
    queryFn: () => apiGet<DeviceOpteemumSummaryPage>(`/devices/${id}/opteemum-summary`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ------------------------------------------------------------------------- */
/* Fila Proactivitee Summary - /api/v2/devices/{id}/proactivitee-summary     */
/* ------------------------------------------------------------------------- */

export interface ProactiviteePerfTile {
  tone: string
  ring: string | null
  ring_length: number | null
  ring_offset: number | null
  icon: string | null
  number: string
  label: string
  more: string
  metric: string | null
  metric_value: number | null
  id: string
  small: boolean
}

export interface ProactiviteeOsTables {
  install_date: string
  last_boot: string
  uptime: string
  changes_24h: number
  os_updates: string
  processes: string
  running_applications: string
  reboot_pending: string
  total_ram: string
  free_ram: string
  ram_usage: string
  total_swap: string
  free_swap: string
  swap_usage: string
  network: string
}

export interface ProactiviteeSecurityRow {
  name: string
  category: string
  enabled: boolean
  up_to_date: boolean
}

export interface ProactiviteeServiceOp {
  role_label: string
  status: string
  others: string[]
  next_text: string
  next_cls: string
  reasons: string[]
  kpi: { display: string; label: string; tone: string } | null
  signals: { label: string; display: string }[]
}

export interface ProactiviteePrimary {
  next_text: string
  next_cls: string
  reasons: string[]
  name: string
  role_label: string
  role_matched: string
  status: string
  status_tone: string
  health_summary: string
  version: string
  version_desc: string
  version_title: string
  version_tone: string
  package: string
  updates: number
  updates_label: string
  updates_more: string
  updates_tone: string
  config_drift: number
  config_items: { path: string; status: string; note: string; badge: string }[]
}

export interface DeviceProactiviteeSummaryPage {
  device: { id: number }
  is_server: boolean
  core: { name: string; focus: string }
  alarms: { critical: number; major: number; minor: number; info: number; closed: number }
  performance: { tiles: ProactiviteePerfTile[] } | null
  live: { has: boolean; [key: string]: unknown }
  thresholds: Record<string, number>
  saturation_pairs: { metric: string; warn: string; crit: string }[]
  os: ProactiviteeOsTables | null
  security: ProactiviteeSecurityRow[]
  service: ProactiviteeServiceOp | null
  primary: ProactiviteePrimary | null
}

export interface ProactiviteeLivePayload {
  has: boolean
  score?: number
  bottleneck?: string
  cpu_util?: number
  cpu_iowait?: number
  cpu_steal?: number
  cpu_load1?: number
  cpu_count?: number
  ram_used_percent?: number
  ram_available?: string
  ram_swap_percent?: number
  ram_swap_used?: string
  ram_swap_total?: string
  disk_util?: number
  disk_await_ms?: number
  disk_read_bps?: string
  disk_write_bps?: string
  gpu_util?: number | string | null
  net_connections?: number
  net_established?: number
  net_in?: number
  net_out?: number
  running_applications?: number | null
  uptime_seconds?: number | null
  updated_at?: string | null
  age_seconds?: number | null
}

export function proactiviteeSummaryQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'proactivitee-summary'],
    queryFn: () => apiGet<DeviceProactiviteeSummaryPage>(`/devices/${id}/proactivitee-summary`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function proactiviteeLiveQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'proactivitee-summary', 'live'],
    queryFn: () => apiGet<ProactiviteeLivePayload>(`/devices/${id}/proactivitee-summary/live`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: 1000,
  })
}

export function proactiviteeWatch(id: number) {
  return apiPost<{ success: boolean; ttl: number }>(`/devices/${id}/proactivitee-summary/watch`, {})
}

/* ------------------------------------------------------------------------- */
/* Fila Configuration - /api/v2/devices/{id}/configuration                   */
/* (perechea lui inventoree/deviceDetails + updateHostRelationship)          */
/* ------------------------------------------------------------------------- */

export interface ConfigLine {
  text: string
  /** `<span class="dcf-muted">N properties</span>` - randul unei componente fara nume. */
  muted: boolean
}

export interface ConfigProperty {
  name: string
  value: string
}

export interface ConfigSpec extends ConfigProperty {
  /** '' valoare scrisa; 'dash' valoare goala (–); 'na' valoare NULL (n/a inclinat). */
  mark: '' | 'dash' | 'na'
}

export interface ConfigCard {
  typeId: number
  icon: string
  title: string
  count: number
  lines: ConfigLine[]
  /** Numai cand e o singura componenta: cele trei proprietati de sub nume. */
  brief: ConfigProperty[]
  more: number
  /** Cate o lista de specificatii pe componenta, sub chevron. */
  details: ConfigSpec[][]
}

export interface ConfigGroup {
  key: string
  label: string
  icon: string
  lead: string
  count: number
  span: number
  cards: ConfigCard[]
}

export interface HostChoice {
  id: number
  name: string
}

export interface HostedGuest {
  id: number
  name: string
  stateKey: string
  state: string
  memory: string
}

export interface UnresolvedGuest {
  name: string
  stateKey: string
  state: string
  memory: string
  suggestedId: number
  suggestedName: string
}

export interface Hosting {
  showHost: boolean
  showVms: boolean
  typeLabel: string
  hostId: number
  hostName: string
  canOpenHost: boolean
  choices: HostChoice[]
  guessedBy: '' | 'discovery' | 'gateway'
  suggested: { id: number; name: string; gateway: string; others: number } | null
  vmCount: number
  guests: HostedGuest[]
  unresolved: UnresolvedGuest[]
  pickerChoices: HostChoice[]
}

export interface DeviceConfigurationPage {
  device: { id: number }
  hosting: Hosting | null
  groups: ConfigGroup[]
}

export function configurationQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'configuration'],
    queryFn: () => apiGet<DeviceConfigurationPage>(`/devices/${id}/configuration`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/** Un profil local de pe masina (ProfileList / passwd / Users), din inventarul agentului. */
export interface LocalProfile {
  sid: string
  /** DOMENIU\cont, sau doar contul */
  name: string
  path: string
  /** ISO, UTC; null cand masina nu stie */
  lastUse: string | null
  loaded: boolean
  temporary: boolean
  roaming: boolean
  mandatory: boolean
}

export interface DeviceLocalProfilesPage {
  device: { id: number }
  profiles: LocalProfile[]
}

export function localProfilesQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'local-profiles'],
    queryFn: () => apiGet<DeviceLocalProfilesPage>(`/devices/${id}/local-profiles`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export type HostLinkAction =
  | { action: 'set-host'; host_device_id: number }
  | { action: 'add-guest'; guest_id: number }
  | { action: 'remove-guest'; guest_id: number }

/** Inventoree::updateHostRelationship - salvarea e pe loc, fara un al doilea Update. */
export function hostLink(id: number, payload: HostLinkAction) {
  return apiPost<{ status: 'OK' }>(`/devices/${id}/host-link`, payload)
}

/**
 * Acelasi cap, dar formularul intreg de pe fila Settings: tipul, gazda si
 * lista de masini gazduite dintr-o data (ramura fara `action` din
 * DeviceConfigurationController::hostLink).
 */
export function saveHostRelationship(id: number, fields: { host_type: string; host_device_id: string; guest_ids: string[] }) {
  return apiPost<{ status: 'OK' }>(`/devices/${id}/host-link`, {
    host_type: fields.host_type,
    host_device_id: fields.host_device_id,
    'guest_ids[]': fields.guest_ids,
  })
}

/* ---------------------------------------------------------------------------
 * Filele Attached Devices si Printers - /api/v2/devices/{id}/attached,
 * /api/v2/devices/{id}/printers (Inventoree::deviceAttached / devicePrinters).
 * ------------------------------------------------------------------------- */

export interface SpecPair {
  name: string
  value: string
  mark: '' | 'dash' | 'na'
}

export interface AttachedItem {
  typeId: number
  icon: string
  title: string
  statusKey: string
  statusLabel: string
  conn: string
  kind: string
  detail: string
  brief: SpecPair[]
  specs: SpecPair[]
}

export interface AttachedHistory {
  action: string
  label: string
  name: string
  when: string
}

export interface AttachedCategory {
  key: string
  label: string
  lead: string
  icon: string
  count: number
  bad: number
  items: AttachedItem[]
  history: AttachedHistory[]
}

export interface DeviceAttachedPage {
  device: { id: number }
  empty: boolean
  categories: AttachedCategory[]
}

export function attachedQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'attached'],
    queryFn: () => apiGet<DeviceAttachedPage>(`/devices/${id}/attached`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export interface PrinterCard {
  title: string
  conn: string
  isDefault: boolean
  stopped: boolean
  stoppedLabel: string
  supplies: string
  hasProblem: boolean
  where: string
  location: string
  driver: string
  detail: string
  specs: SpecPair[]
}

export interface DevicePrintersPage {
  device: { id: number }
  empty: boolean
  physical: PrinterCard[]
  network: PrinterCard[]
}

export function printersQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'printers'],
    queryFn: () => apiGet<DevicePrintersPage>(`/devices/${id}/printers`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Performance And Upgrades - /api/v2/devices/{id}/performance si
 * /performance/alarms (Inventoree::deviceAlarms / alarmsList).
 * ------------------------------------------------------------------------- */

export interface PerformanceKind {
  label: string
  color: string
  virtual: boolean
  tip: number
  note: string
}

export interface PerformanceCostly {
  key: string
  label: string
  have: string
  want: string
  cost: number
  virtualAge: boolean
}

export interface PerformanceUnknown {
  key: string
  label: string
}

export interface PerformanceScore {
  score: number | null
  band: string
  label: string
  color: string
  costly: PerformanceCostly[]
  unknown: PerformanceUnknown[]
}

export interface DevicePerformancePage {
  device: { id: number }
  kind: PerformanceKind
  score: PerformanceScore | null
}

export function performanceQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'performance'],
    queryFn: () => apiGet<DevicePerformancePage>(`/devices/${id}/performance`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export type AlarmFlag = 'all' | '0' | '1' | '2' | '3'

export interface UpgradeAlarm {
  id: number
  details: string
  flag: number
  color: string
  when: string
  age: string
  ageColor: string
}

export interface DevicePerformanceAlarms {
  flag: AlarmFlag | string
  alarms: UpgradeAlarm[]
}

export function performanceAlarmsQuery(id: number, flag: AlarmFlag) {
  return queryOptions({
    queryKey: ['device', id, 'performance', 'alarms', flag],
    queryFn: () => apiGet<DevicePerformanceAlarms>(`/devices/${id}/performance/alarms?flag=${encodeURIComponent(flag)}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Warranty - /api/v2/devices/{id}/warranty
 * (Inventoree::deviceWarrantyAndFiles / updateDeviceDetails).
 * ------------------------------------------------------------------------- */

export interface WarrantyForm {
  brandId: string
  serial: string
  inventory: string
  location: string
  warrantyExpiry: string
  extendedExpiry: string
  acquisition: string
  serviceTag: string
}

export interface WarrantyHp {
  sn: string
  serviceType: string
  type: string
  status: string
  statusTone: 'color-red' | 'color-green' | string
  startDate: string
  endDate: string
  serviceLevel: string
}

export interface DeviceWarrantyPage {
  device: { id: number }
  form: WarrantyForm
  brands: { id: number; name: string }[]
  hp: WarrantyHp | null
}

export function warrantyQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'warranty'],
    queryFn: () => apiGet<DeviceWarrantyPage>(`/devices/${id}/warranty`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function saveWarranty(id: number, body: FormData) {
  return apiPostForm<{ status: 'OK' }>(`/devices/${id}/warranty`, body)
}

/* ---------------------------------------------------------------------------
 * Fila Hardware Changes - /api/v2/devices/{id}/hardware-events
 * (Inventoree::deviceEvents / getDeviceEvents).
 * ------------------------------------------------------------------------- */

export type HardwareCategory = '' | 'critical' | 'major' | 'minor'

export interface DeviceHardwareEventsPage {
  device: { id: number }
  categories: Record<'critical' | 'major' | 'minor', string>
  active: HardwareCategory
}

export interface HardwareEventRow {
  DT_RowId: string
  date: string
  message: string
  user: string
  authorised: string
  category: string
  /** `initial` pentru randul de inventar dintai, `event` pentru restul (HardwareEventRow::present). */
  kind: string
}

export interface HardwareEventsTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: HardwareEventRow[]
  counts: { all: number; critical: number; major: number; minor: number }
}

export function hardwareEventsPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'hardware-events'],
    queryFn: () => apiGet<DeviceHardwareEventsPage>(`/devices/${id}/hardware-events`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila CMDB Relation - /api/v2/devices/{id}/cmdb (Cmdb::deviceView).
 * ------------------------------------------------------------------------- */

export interface DeviceCmdbCi {
  id: number
  name: string
  className: string
  companyName: string
  kind: string
  status: string
  statusPill: string
  refType: number
  refId: number
  deviceName: string
}

export interface DeviceCmdbEdge {
  id: number
  direction: 'out' | 'in'
  relation: string
  itemId: number
  itemName: string
  itemClass: string
  origin: string
}

export interface DeviceCmdbImpact {
  id: number
  name: string
  className: string
  depth: number
  importance: { level: number; label: string; class: string }
}

export interface DeviceCmdbTicket {
  id: number
  subject: string
  opened: string
  state: { label: string; pill: string; resolved: string | null }
}

export interface DeviceCmdbAudit {
  when: string
  who: string
  what: string
  from: string
  to: string
}

export interface DeviceCmdbPage {
  device: { id: number }
  empty: boolean
  message: string | null
  ci: DeviceCmdbCi | null
  canWrite: boolean
  relationTypes: { id: number; name: string }[]
  outgoing: DeviceCmdbEdge[]
  incoming: DeviceCmdbEdge[]
  impact: DeviceCmdbImpact[]
  tickets: DeviceCmdbTicket[]
  ticketLimit: number
  openTickets: number
  audit: DeviceCmdbAudit[]
}

/* ---------------------------------------------------------------------- *
 * "Review All" - Fleet::review + _partials/fleet_review.php
 * ---------------------------------------------------------------------- */

export interface FleetReviewRow {
  id: number
  name: string
  os: string
  seen: string
  tone: 'bad' | 'unknown' | 'ok'
  note: string
  values: Record<string, number>
}

export interface FleetReviewPage {
  /** Ne-gol cand nu e nimic de aratat (cheie necunoscuta, lista nu s-a putut face). */
  failed: string
  title?: string
  question?: string
  columns?: { key: string; label: string; align: 'left' | 'right' }[]
  rows?: FleetReviewRow[]
  summary?: { value: number; label: string; tone: string }[]
  company?: string
  anchor?: number
}

export function fleetReviewQuery(id: number, key: string) {
  return queryOptions({
    queryKey: ['device', id, 'fleet-review', key],
    queryFn: () => apiGet<FleetReviewPage>(`/devices/${id}/fleet-review/${key}`),
    staleTime: 30 * 1000,
  })
}

export function cmdbQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'cmdb'],
    queryFn: () => apiGet<DeviceCmdbPage>(`/devices/${id}/cmdb`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function cmdbSearch(id: number, q: string) {
  return apiGet<{ id: number; name: string; class: string }[]>(
    `/devices/${id}/cmdb/search?q=${encodeURIComponent(q)}`,
  )
}

export function cmdbAddRelation(id: number, targetCiId: number, typeId: number) {
  return apiPost<{ status: string; message: string; id?: number }>(`/devices/${id}/cmdb/relations`, {
    target_ci_id: targetCiId,
    relation_type_id: typeId,
  })
}

export function cmdbRemoveRelation(id: number, relId: number) {
  return apiPost<{ status: string; message: string }>(`/devices/${id}/cmdb/relations/${relId}/remove`, {})
}

/* ---------------------------------------------------------------------------
 * Fila Agent Settings - /api/v2/devices/{id}/agent-settings
 * (Overview::agentSettings / agentFunctions).
 * ------------------------------------------------------------------------- */

export interface AgentSettingsTab {
  key: string
  label: string
  icon: string
  href: string
  badge: string | number | null
  badgeTitle: string
  badgeTone: string
}

export interface DeviceAgentScanning {
  on: boolean
  recurrence: boolean
  recurrence_interval: number
  recurrence_moment: number
  recurrence_time: string
  filter_type: number
  custom_filter: string
  ip_from: string
  ip_to: string
  domain_user: string
  hasPassword: boolean
}

export interface DeviceAgentSettingsPage {
  device: { id: number; type: number }
  hideSettings: boolean
  isLinux: boolean
  badges: { health: string | null; agent_log: number | null; operations: number | null; discovered: number | null }
  functions: { networkMonitor: boolean; configRelay: boolean; domainController?: boolean }
  scanning: DeviceAgentScanning
  tabs: AgentSettingsTab[]
}

export function agentSettingsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'agent-settings'],
    queryFn: () => apiGet<DeviceAgentSettingsPage>(`/devices/${id}/agent-settings`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function saveAgentFunctions(id: number, flags: { networkMonitor: boolean; configRelay: boolean }) {
  return apiPost<DeviceAgentSettingsPage>(`/devices/${id}/agent-settings/functions`, {
    network_monitor: flags.networkMonitor ? 1 : 0,
    config_relay: flags.configRelay ? 1 : 0,
  })
}

/**
 * Porneste deploy-ul prin Group Policy de pe un controlor de domeniu. Adresa
 * portalului o da browserul - serverul verifica sa fie chiar gazda cererii.
 */
export function startGpoDeploy(id: number, ous: string[] = [], kind: GpoKind = 'both') {
  return apiPost<{ queued: boolean; pending: boolean }>(`/devices/${id}/agent-settings/gpo`, {
    origin: window.location.origin,
    ous,
    kind,
  })
}

export interface DomainOu {
  dn: string
  name: string
}

export type GpoMachineStage = 'started' | 'installed' | 'failed' | 'present' | 'excluded'

/** Pe ce fel de masini pleaca deploy-ul (filtrul din Where to deploy). */
export type GpoKind = 'both' | 'servers' | 'workstations'

export interface DomainMissingComputer {
  name: string
  os: string
  lastLogon: string
  dn: string
  /** Ce a raportat masina despre instalarea prin GPO; null = n-a raportat nimic. */
  gpo: { stage: GpoMachineStage; message: string; reportedAt: string } | null
}

/** Un calculator din AD care are deja agentul (pentru lista celor din afara selectiei). */
export interface DomainAgentComputer {
  name: string
  os: string
  lastLogon: string
  dn: string
}

export type GpoRunState = 'waiting' | 'running' | 'succeeded' | 'failed'

/**
 * Acoperirea pe o bucata de domeniu: calculatoarele active dintr-un container
 * (`parent` = DN-ul parinte), de un fel (server / statie). Ecranul aduna
 * bucatile de sub OU-urile bifate (DomainCoverage::compute, in v4/api).
 */
export interface DomainCoverageGroup {
  parent: string
  server: boolean
  total: number
  covered: number
  failed: number
  started: number
  installed: number
  present: number
  excluded: number
  notReached: number
}

/**
 * Ce stie portalul despre domeniul unui controlor: OU-urile raportate de
 * comanda ADI, acoperirea (calculatoare active din AD vs. aparate cu agent) si
 * ultima comanda GPO. `domain`/`coverage` sunt null pana la prima citire.
 */
export interface DomainPanel {
  domain: { name: string; collectedAt: string; ous: DomainOu[] } | null
  coverage: {
    total: number
    covered: number
    disabled: number
    missing: DomainMissingComputer[]
    missingTotal: number
    /** Calculatoarele care au agentul, taiate la acelasi plafon ca `missing`. Lipsesc la raspunsurile vechi. */
    withAgent?: DomainAgentComputer[]
    withAgentTotal?: number
    gpo: { failed: number; started: number; installed: number; present: number; excluded: number; notReached: number }
    groups: DomainCoverageGroup[]
  } | null
  /** Numele controlerului insusi, de pe care pleaca politica ('' = necunoscut). */
  source?: string
  lastRun: { state: GpoRunState; message: string; createdAt: string; answeredAt: string; ous: string[]; kind: GpoKind } | null
  readingPending: boolean
  deployPending: boolean
}

/** Cat timp asteapta ceva de la agent, panoul se reciteste singur. */
export function domainPanelQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'agent-domain'],
    queryFn: () => apiGet<DomainPanel>(`/devices/${id}/agent-settings/domain`),
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const d = query.state.data
      if (!d) return false
      const busy = d.readingPending || d.deployPending || d.lastRun?.state === 'waiting' || d.lastRun?.state === 'running'
      return busy ? 10000 : false
    },
  })
}

/** Cere controlorului sa citeasca din nou Active Directory (comanda ADI). */
export function refreshAdInventory(id: number) {
  return apiPost<{ queued: boolean; pending: boolean }>(`/devices/${id}/agent-settings/ad-inventory`, {})
}

export type AgentHealthState = 'current' | 'outdated' | 'beta' | 'unknown' | 'missing'
export type AgentHealthRange = '1h' | '24h' | '7d' | '30d' | string

export interface DeviceAgentHealth {
  device: { id: number }
  isLinux: boolean
  canManageUnix: boolean
  installed: string
  target: string
  beta: string
  state: AgentHealthState
  lastAgentUpdate: string | null
  lastSeen: string
  updateMode: number
  forceUpdatePending: boolean
  forceUpdateVersion: string
  canUpdate: boolean
  lastSample: { cpu: number | null; memory: number | null; at: string | null }
  cpuLabel: string
  memLabel: string
}

export interface AgentHealthPoint {
  date: string
  od_service_cpu: number | null
  od_service_memory: number | null
  optimumdesk_cpu: number | null
  optimumdesk_memory: number | null
  max_cpu?: number | null
  max_memory?: number | null
}

export interface DeviceAgentHealthSeries {
  range: string
  points: AgentHealthPoint[]
}

export function agentHealthQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'agent-settings', 'health'],
    queryFn: () => apiGet<DeviceAgentHealth>(`/devices/${id}/agent-settings/health`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function agentHealthSeriesQuery(id: number, range: AgentHealthRange) {
  return queryOptions({
    queryKey: ['device', id, 'agent-settings', 'health', 'series', range],
    queryFn: () => apiGet<DeviceAgentHealthSeries>(`/devices/${id}/agent-settings/health/series?range=${encodeURIComponent(range)}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function saveAgentAutoUpdate(id: number, mode: number) {
  return apiPost<DeviceAgentHealth>(`/devices/${id}/agent-settings/auto-update`, { mode })
}

export function forceAgentUpdate(id: number, version: string) {
  return apiPost<{ status: string; version: string }>(`/devices/${id}/agent-settings/force-update`, {
    new_version: version,
  })
}

/**
 * `Overview::checkDeviceForceUpdateStatus` - `updating` cat e o cerere de
 * actualizare deschisa. Il intreaba caseta masinii din tichet din 10 in 10
 * secunde (`owners_and_devices-1.js`), ca butonul sa se dezlege singur.
 */
export function forceUpdateStatus(id: number) {
  return apiGet<{ status: string }>(`/devices/${id}/agent-settings/force-update/status`)
}

/**
 * `Overview::deviceSeen` - de cate secunde n-a mai raportat masina. `ok: false`
 * cand nu e in vedere sau n-a raportat niciodata.
 */
export function deviceSeen(id: number) {
  return apiGet<{ ok: boolean; age_seconds?: number }>(`/devices/${id}/seen`)
}

export async function retrieveAgentLogs(id: number, email: string) {
  const body = new URLSearchParams()
  body.set('logs_email', email)
  const response = await fetch(`/api/v2/devices/${id}/agent-settings/retrieve-logs`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json, text/plain', 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (response.status === 401) {
    const text = await response.text()
    if (text.includes('Email address invalid')) {
      throw new ApiError(401, 'Email address invalid')
    }
    sendToLogin()
    throw new ApiError(401, 'Not logged in')
  }
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`
    try {
      const json = (await response.json()) as { message?: string }
      if (json.message) message = json.message
    } catch {
      /* corpul nu e JSON */
    }
    throw new ApiError(response.status, message)
  }
  return (await response.json()) as { ok: boolean }
}

export function saveAgentScanning(id: number, fields: Record<string, string | number>) {
  return apiPost<DeviceAgentSettingsPage>(`/devices/${id}/agent-settings/scanning`, fields)
}

export interface DeviceAgentModuleRow {
  id: number
  name: string
  free: boolean
  freeDisabled: boolean
  premium: boolean
  menu: boolean
  menuDisabled: boolean
  extended: boolean | null
  /**
   * XDR pe aparat (`it_edr_settings`, starea efectiva): numai pe randul
   * Securitee (modulul 9); null pe restul si cand tabela EDR lipseste.
   */
  /** XDR pe aparat: follow (implicit) / on / off fortate; null = fara tabela EDR. */
  xdr: XdrMode | null
  /** Ce primeste aparatul si de la cine (device / group / company). */
  xdr_state?: { on: boolean; from: 'device' | 'group' | 'company'; group: string | null } | null
  /** Ce ar primi aparatul pe Follow (de la grup / firma). */
  xdr_follow?: { on: boolean; from: 'device' | 'group' | 'company'; group: string | null } | null
  /** Productivitee (coloana Compliance): are nivel de colectare si acord. */
  productivitee: boolean
  /** Nivelul curent de colectare Productivitee (1 Basic, 2 Extended), null in rest. */
  level: number | null
}

export interface DeviceAgentModulesPage {
  exists: boolean
  device: { id: number }
  remoteControl: boolean
  catalog: DeviceAgentModuleRow[]
  /** Bifa XDR se schimba numai cu `securitee.xdr_settings.edit`; lipsa = da (server vechi). */
  can_edit_xdr?: boolean
}

export function agentModulesQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'agent-settings', 'modules'],
    queryFn: () => apiGet<DeviceAgentModulesPage>(`/devices/${id}/agent-settings/modules`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/** Bifa XDR din randul Securitee: randul aparatului in it_edr_settings. */
export type XdrMode = 'follow' | 'on' | 'off'

export function saveAgentModuleXdr(id: number, mode: XdrMode) {
  return apiPost<DeviceAgentModulesPage>(`/devices/${id}/agent-settings/modules/xdr`, { mode })
}

export function saveAgentModule(id: number, module: number, status: string) {
  return apiPost<DeviceAgentModulesPage>(`/devices/${id}/agent-settings/modules`, { module, status })
}

export function saveAgentModuleMenu(id: number, module: number, status: string) {
  return apiPost<DeviceAgentModulesPage>(`/devices/${id}/agent-settings/modules/menu`, { module, status })
}

/**
 * Aprinderea / stingerea lui Productivitee cu nivel de colectare (1 Basic,
 * 2 Extended) si acord. La aprindere, `consent` e obligatoriu; nivelul Extended
 * cere in plus `consentExtended`. Serverul respinge (422) fara ele.
 */
export interface ProductiviteeInput {
  module: number
  status: 'free' | 'premium' | 'disabled'
  level: number
  consent: boolean
  consentExtended: boolean
}

export function saveAgentProductivitee(id: number, input: ProductiviteeInput) {
  return apiPost<DeviceAgentModulesPage>(`/devices/${id}/agent-settings/modules/productivitee`, {
    module: input.module,
    status: input.status,
    level: input.level,
    consent: input.consent ? 1 : 0,
    consent_extended: input.consentExtended ? 1 : 0,
  })
}

export interface AgentLogRow {
  ID: number
  device_id: number
  occurred_at: string
  step: string
  message: string
  level: string
  DT_RowId: string
}

export interface DeviceAgentLogTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: AgentLogRow[]
}

export function agentLogQuery(
  id: number,
  params: { start: number; length: number; orderColumn: number; orderDir: 'asc' | 'desc'; search: string },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  q.set('search[value]', params.search)
  return queryOptions({
    queryKey: ['device', id, 'agent-settings', 'log', params],
    queryFn: () => apiGet<DeviceAgentLogTable>(`/devices/${id}/agent-settings/log?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export interface DeviceAgentAppearancePage {
  device: { id: number; type: number }
  hideSettings: boolean
  appearance: { desktopShortcut: boolean; applicationIcon: boolean }
  tabs: AgentSettingsTab[]
}

export function agentDeviceSettingsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'agent-settings', 'settings'],
    queryFn: () => apiGet<DeviceAgentAppearancePage>(`/devices/${id}/agent-settings/settings`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function saveAgentAppearance(id: number, status: string) {
  return apiPost<DeviceAgentAppearancePage>(`/devices/${id}/agent-settings/appearance`, { status })
}

export interface DeviceAgentAssignmentPage {
  device: { id: number }
  owner: { id: number; name: string }
  areaId: number
  officeId: number
  groupId: number
  classified: string
  autoUpdate: boolean
  beta: boolean
  owners: { id: number; text: string }[]
  areas: { id: number; name: string }[]
  offices: { id: number; name: string }[]
  groups: { id: number; name: string }[]
}

export function agentAssignmentQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'agent-settings', 'assignment'],
    queryFn: () => apiGet<DeviceAgentAssignmentPage>(`/devices/${id}/agent-settings/assignment`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function saveAgentAssignment(id: number, fields: Record<string, string | number>) {
  return apiPost<DeviceAgentAssignmentPage>(`/devices/${id}/agent-settings/assignment`, fields)
}

export interface DeviceAgentDiscoveryPage {
  device: { id: number }
  isLinux: boolean
  tabs: AgentSettingsTab[]
}

export function agentDiscoveryQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'agent-settings', 'discovery'],
    queryFn: () => apiGet<DeviceAgentDiscoveryPage>(`/devices/${id}/agent-settings/discovery`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export interface AgentDiscoveryDevice {
  id: number
  name: string
  ip: string
  mac: string
  os: string
  manufacturer: string
  manufacturer_id: number
  type: string
  type_id: number
  services: string[]
  ports: string
  banners: string
  port_details: { port: number; proto: string; name: string; banner: string }[]
  seen: string
}

export interface AgentDiscoveryCommand {
  id: number
  type: string
  at: string
  state: string
  output: string
}

export interface DeviceAgentDiscoveryList {
  device: { id: number }
  kind: 'devices' | 'services' | 'extended' | 'users'
  commands?: AgentDiscoveryCommand[]
  devices?: AgentDiscoveryDevice[]
  types?: { id: number; name: string }[]
  manufacturers?: { id: number; name: string }[]
  settings?: {
    custom_filter: string
    recurrence: boolean
    recurrence_interval: number
    recurrence_moment: number
    recurrence_time: string
  }
}

export function agentDiscoveryListQuery(id: number, kind: string) {
  return queryOptions({
    queryKey: ['device', id, 'agent-settings', 'discovery', kind],
    queryFn: () => apiGet<DeviceAgentDiscoveryList>(`/devices/${id}/agent-settings/discovery/${kind}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function scanAgentDiscovery(id: number, kind: string) {
  return apiPost<DeviceAgentDiscoveryList>(`/devices/${id}/agent-settings/discovery/${kind}/scan`, {})
}

export function saveAgentDiscoveryUsers(id: number, fields: Record<string, string | number>) {
  return apiPost<DeviceAgentDiscoveryList>(`/devices/${id}/agent-settings/discovery/users`, fields)
}

/** Discovery::saveDiscoverServicesSettings / saveDiscoverExtendedSettings (tip 3 / 4). */
export function saveAgentDiscoveryKind(id: number, kind: 'services' | 'extended', fields: Record<string, string | number>) {
  return apiPost<DeviceAgentDiscoveryList>(`/devices/${id}/agent-settings/discovery/${kind}`, fields)
}

export interface DeviceAgentScanOption {
  key: string
  name: string
  required: boolean
  extended: boolean
  requiredOptions: string
}

export interface DeviceAgentOperationsPage {
  device: { id: number }
  report: string
  isLinux: boolean
  /** Dreptul de a apasa Run Scan (`DiagnosticRun::RIGHT`), unul pentru toate trei scanarile. */
  canRun: boolean
  options: DeviceAgentScanOption[]
}

export interface DiagnosticRunDone {
  ok: boolean
  ticket?: number
  command?: string
  error?: string
  message?: string
}

/** `Diagnostic_reports::runDiagnostic`: tichetul + SPD + SDI/SND/SSD pe masina. */
export function runDiagnostic(id: number, report: string, options: string[], extended: Record<string, string> = {}): Promise<DiagnosticRunDone> {
  return apiPost<DiagnosticRunDone>(`/devices/${id}/diagnostics/run`, { report, 'options[]': options, ...extended })
}

/**
 * Panoul unei scanari de diagnostic. `report` alege scanarea, iar capatul cere
 * dreptul ei (`network_scan` -> overview.network_scan.read, `security_scan` ->
 * securitee.security_scan.read), ca in `Diagnostic_reports::device_scan`.
 */
export function agentOperationsQuery(id: number, report = 'network_scan') {
  return queryOptions({
    queryKey: ['device', id, 'agent-settings', 'operations', report],
    queryFn: () => apiGet<DeviceAgentOperationsPage>(`/devices/${id}/agent-settings/operations?report=${encodeURIComponent(report)}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function hardwareEventsTableQuery(
  id: number,
  params: {
    start: number
    length: number
    orderColumn: number
    orderDir: 'asc' | 'desc'
    search: string
    category: HardwareCategory
    from: string
    to: string
  },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  q.set('search[value]', params.search)
  q.set('category', params.category)
  q.set('from', params.from)
  q.set('to', params.to)
  return queryOptions({
    queryKey: ['device', id, 'hardware-events', 'table', params],
    queryFn: () => apiGet<HardwareEventsTable>(`/devices/${id}/hardware-events/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Softsee Software Changes - /api/v2/devices/{id}/softsee-events
 * ------------------------------------------------------------------------- */

export type SoftseeEventCategory =
  | ''
  | 'critical'
  | 'installed'
  | 'removed'
  | 'upgraded'
  | 'downgraded'
  | 'changed'
  | 'other'

export interface SoftseeEventRow {
  DT_RowId: string
  date: string
  message: string
  user: string
  category: string
  /** `initial` pe linia de pornire (lotul de la prima inventariere, pliat), `event` in rest. */
  kind?: string
}

export interface DeviceSoftseeEventsPage {
  device: { id: number }
  categories: Record<string, string>
  active: string
}

export interface SoftseeEventsTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  counts: Record<string, number>
  data: SoftseeEventRow[]
}

export function softseeEventsPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'softsee-events'],
    queryFn: () => apiGet<DeviceSoftseeEventsPage>(`/devices/${id}/softsee-events`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function softseeEventsTableQuery(
  id: number,
  params: {
    start: number
    length: number
    orderColumn: number
    orderDir: 'asc' | 'desc'
    search: string
    category: SoftseeEventCategory
    from: string
    to: string
  },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  q.set('search[value]', params.search)
  q.set('category', params.category)
  q.set('from', params.from)
  q.set('to', params.to)
  return queryOptions({
    queryKey: ['device', id, 'softsee-events', 'table', params],
    queryFn: () => apiGet<SoftseeEventsTable>(`/devices/${id}/softsee-events/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/** Softsee::deviceInitialEvents - ce s-a gasit la prima inventariere de programe, sub chevronul liniei de pornire. */
/** Opteemum::deviceInitialEvents - lotul `health` (prima proba CPU / memorie / disc). */
export function opteemumInitialEventsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'opteemum-initial-events'],
    queryFn: () => apiGet<{ events: { message: string }[] }>(`/devices/${id}/opteemum-initial-events`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/** Proactivitee::deviceInitialEvents - lotul `monitoring` (setarile de monitorizare). */
export function proactiviteeInitialEventsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'proactivitee-initial-events'],
    queryFn: () => apiGet<{ events: { message: string }[] }>(`/devices/${id}/proactivitee-initial-events`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function softseeInitialEventsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'softsee-initial-events'],
    queryFn: () => apiGet<{ events: { message: string }[] }>(`/devices/${id}/softsee-initial-events`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Softsee Used Applications - /api/v2/devices/{id}/softsee-used-apps
 * ------------------------------------------------------------------------- */

export interface DeviceSoftseeUsedAppsPage {
  device: { id: number }
  period_days: 1 | 7 | 30
}

export interface SoftseeUsedAppRow {
  DT_RowId: string
  ID: number
  name: string
  focus_time: string
  last_access: string
}

/** Tile-ul Most Used: varful perioadei dupa Focus Time, fara cautare. */
export interface SoftseeUsedAppsMostUsed {
  name: string
  seconds: number
  /** Secundele scrise ca Focus Time din lista (h:mm:ss / m:ss). */
  focus_time: string
}

export interface SoftseeUsedAppsTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  period_days: 1 | 7 | 30
  data: SoftseeUsedAppRow[]
  /** null = nicio mostra de focus in perioada. */
  most_used: SoftseeUsedAppsMostUsed | null
}

export function softseeUsedAppsPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'softsee-used-apps'],
    queryFn: () => apiGet<DeviceSoftseeUsedAppsPage>(`/devices/${id}/softsee-used-apps`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function softseeUsedAppsTableQuery(
  id: number,
  params: {
    start: number
    length: number
    search: string
    periodDays: 1 | 7 | 30
  },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('search[value]', params.search)
  q.set('period_days', String(params.periodDays))
  return queryOptions({
    queryKey: ['device', id, 'softsee-used-apps', 'table', params],
    queryFn: () => apiGet<SoftseeUsedAppsTable>(`/devices/${id}/softsee-used-apps/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Disk Management - /api/v2/devices/{id}/disks (Opteemum::deviceHddDetails
 * + hddDetails.php). Contractul e storages + summary + banner + failed.
 * ------------------------------------------------------------------------- */

export interface DiskPieSlice {
  name: string
  size: number
}

export interface DiskAttribute {
  name: string
  current: string | number
  worst: string | number
  treshold: string | number
  data: string | number
  is_ok: string | number
}

export interface DiskForecast {
  label: string
  rate: string
  hours: number | null
  inode: number | null
}

export interface DiskPartition {
  name: string
  size: number | string
  free_space: number | string
  file_system: string
  volume_serial_number: string
  primary: number
  forecast: DiskForecast
}

export interface SmartStorage {
  ID: number
  index: number | string
  x_deviceid: string
  name: string
  size: number | string
  unallocated_space: number | string
  temperature: number | string
  is_ok: number
  status: string
  pie: DiskPieSlice[]
  attributes: DiskAttribute[]
  partitions: DiskPartition[]
}

export interface SummaryDisk {
  index: number | string
  size: number | string
  unallocated_space: number | string
  is_ok: number
  status: string
  current_temperature: number | string
}

export interface SmartBanner {
  state: 'errors' | 'ok' | 'unsupported' | 'none'
  count: number
  checked: number
  drives: number
  disks_bad: string[]
  unsupported: string[]
  failures: {
    disk: string
    name: string
    current: string | number
    worst: string | number
    threshold: string | number
    data: string | number
  }[]
}

export interface DeviceDisksPage {
  storages: SmartStorage[]
  summary: SummaryDisk[]
  banner: SmartBanner | null
  failed: string
}

export function disksQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'disks'],
    queryFn: () => apiGet<DeviceDisksPage>(`/devices/${id}/disks`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ActiveAppRow {
  DT_RowId: string
  ID: number
  application_name: string
  application_id: string
  cpu_load: number | string
  memory_used: number | string
  priority: string
  threads: number | string
  io_per_sec: number | string
  closed: number
  can_kill: number
  closeForm: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ActiveAppsTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: ActiveAppRow[]
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Softsee Application Management - /api/v2/devices/{id}/app-management
 * (Opteemum::applicationManagement + deviceApplications +
 * installedApplicationsData)
 * ------------------------------------------------------------------------- */

export interface AppChip {
  key: string
  label: string
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export interface AppManagementRow {
  DT_RowId: string
  ID: number
  cells: string[]
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export interface AppManagementTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  applications_type: string
  data: AppManagementRow[]
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export interface AppTab {
  label: string
  icon: string
  key: string
  href?: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ------------------------------------------------------------------------- */
/* Filele Proactivitee: Active Applications / Process Load / Sessions        */
/* ------------------------------------------------------------------------- */

export interface ChartPoint {
  name: string
  value: number
}


/* union from /tmp/v4-inventoree-hard/v4/web/src/api/devices.ts */
export interface CommandResultPage {
  empty: boolean
  status: string
  output: string
  scan: { summary: string; hosts: CommandScanHost[] } | null
}


/* union from /tmp/v4-inventoree-card/v4/web/src/api/devices.ts */
export interface CommandScanHost {
  ip: string
  mac: string
  name: string
  ports: string[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ConfigRelayCredential {
  id: number
  user: string
  description: string
  device_id: number
  discovered_id: number
  source: string
  device_name: string
  device_ip: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ConfigRelayHistoryRow {
  id: number
  host: string
  user: string
  command: string
  state: string
  result: string
  created: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ConfigRelayQueueResult {
  ok: boolean
  error?: string
  command_id?: number
  host?: string
  user?: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Configuration Relay - /api/v2/devices/{id}/config-relay
 * ------------------------------------------------------------------------- */

export interface ConfigRelayTarget {
  id: number
  name: string
  managed: boolean
  type_name: string
  ip: string
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export interface CustomField {
  id: number
  label: string
  field_type: number
  value: string
  options: string[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceActiveAppsPage {
  device: { id: number }
  chart: ChartPoint[]
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export interface DeviceAgentlessForm {
  name: string
  typeId: string
  subtypeId: string
  ownerId: string
  ownerName: string
  officeId: string
  groupId: string
  brandId: string
  model: string
  serial: string
  classified: string
  virtual: boolean
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export interface DeviceAgentlessPage {
  device: { id: number }
  form: DeviceAgentlessForm
  types: NamedId[]
  subTypes: NamedId[]
  owners: NamedId[]
  offices: NamedId[]
  groups: NamedId[]
  brands: NamedId[]
  customFields: CustomField[]
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export interface DeviceAppManagementPage {
  device: { id: number }
  applications_type: string
  groups: AppChip[]
  tags: AppChip[]
  tabs: AppTab[]
}


/* union from /tmp/v4-inventoree-hard/v4/web/src/api/devices.ts */
export interface DeviceCardSettingsPage {
  device: { id: number }
  form: {
    ownerId: number
    areaId: number
    officeId: number
    groupId: number
    classified: string
    autoUpdate: boolean
    beta: boolean
    adRelay: boolean
  }
  owners: { id: number; name: string }[]
  areas: { id: number; name: string }[]
  offices: { id: number; name: string }[]
  groups: { id: number; name: string }[]
  host: {
    host_type: string
    host_device_id: number
    guests: { id: number; name: string }[]
    choices: { id: number; name: string }[]
    types: { code: string; label: string }[]
  }
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Scoica Remote Command Prompt - /api/v2/devices/{id}/cmd
 * ------------------------------------------------------------------------- */

export interface DeviceCmdPage {
  device: { id: number }
  available: boolean
  asleep: boolean
  linux: boolean
  os_family: string
}


/* union from /tmp/v4-inventoree-card/v4/web/src/api/devices.ts */
export interface DeviceCommandResult {
  empty: boolean
  status: string
  output: string
  scan: { summary: string; hosts: CommandScanHost[] } | null
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceConfigRelayPage {
  device: { id: number }
  available: boolean
  targets: ConfigRelayTarget[]
  targets_by_id: Record<string, ConfigRelayTarget>
  credentials: ConfigRelayCredential[]
  history: ConfigRelayHistoryRow[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Core Services - /api/v2/devices/{id}/core-services
 * ------------------------------------------------------------------------- */

export interface DeviceCoreServicesPage {
  available: boolean
  is_server: boolean
  device?: { id: number }
  role?: { label: string; foot: string; others: string[] }
  status?: { tone: string; label: string; foot: string; title: string }
  focus?: { tone: string; display: string; small: boolean; foot: string }
  upgrade?: { tone: string; display: string; title: string; foot: string }
  next?: { text: string; cls: string } | null
  reasons?: { text: string; severity: string; icon: string }[]
  show_hosted_vms: boolean
  hosted_vms: {
    name: string
    device_id: number
    href: string
    power: string
    state_label: string
    tone: string
    health: string
    memory: string
  }[]
  primary?: {
    name: string
    package: string
    version: string
    available: string
    importance: string
    updates: string
  }
  signals?: { label: string; display: string }[]
  config?: { score: string; items: { path: string; note: string }[] } | null
  optimizations?: string[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Remote Event Viewer - /api/v2/devices/{id}/proactivitee-event-viewer
 * ------------------------------------------------------------------------- */

export interface DeviceEventViewerPage {
  device: { id: number }
  archivedCount: number
  empty: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceExtendedMonitoringPage {
  device: { id: number }
  tabs: ExtendedTab[]
  network_statistics: {
    allowed: boolean
    last?: {
      name: string
      bytes_received: string
      bytes_sent: string
      bytes_total: string
      bandwidth: string
      packets_out_errors: string
      packets_in_errors: string
      date: string
    }[]
    charts?: MonitoringCharts
  }
  network_latency: { allowed: boolean; charts?: MonitoringCharts }
  process_load: {
    allowed: boolean
    rows?: {
      resource_name: string
      cpu: string
      memory: string
      created: string
      killed: boolean
      device_id: number
    }[]
  }
  stopped_processes: {
    allowed: boolean
    rows?: {
      name: string
      display_name: string
      exit_code: string
      description: string
      created: string
      restarted: boolean
      device_id: number
    }[]
  }
  disc_io: { allowed: boolean; charts?: MonitoringCharts }
  current_sessions: {
    allowed: boolean
    sessions?: {
      id: string
      user_name: string
      domain: string
      sid: string
      login_type: string
      logon_id: string
      local_path: string
      local_path_size: string
      roaming_profile: string
      roaming_profile_path: string
      roaming_profile_path_size: string
      logged_off: boolean
    }[]
    logins?: {
      user: string
      type: string
      at: string
      open: boolean
      seconds: number | null
      left_at: string
      duration: string
    }[]
  }
  hdd_smart: {
    allowed: boolean
    disks?: {
      index: string
      attributes: { name: string; current: string; worst: string; treshold: string; data: string; is_ok: boolean }[]
    }[]
  }
  encryption: {
    allowed: boolean
    lead?: string
    unprotected?: number
    volumes?: {
      protected: boolean
      title_name: string
      letter: string
      chip: { text: string; tone: string }
      size: string
      status: string
      encryption: string
      protection: string
      lock: string
      auto_unlock: string
      id_field: string
      percent: number
    }[]
  }
  raid: {
    allowed: boolean
    arrays?: {
      label_name: string
      array_id: string
      level: string
      state: string
      badge: { text: string; icon: string; tone: string }
      disks: string
      failed: number
      source: string
      controller: string
      detail: string
      updated: string
    }[]
    volumes?: {
      volume: string
      letter: string
      label: string
      file_system: string
      type: string
      size: string
      status: string
      healthy: boolean
      info: string
      date: string
    }[]
    hp?: {
      slot: string
      port: string
      box: string
      bay: string
      size: string
      status: string
      ok: boolean
      date: string
    }[]
    widget?: string
    empty?: boolean
  }
  backup_jobs: {
    allowed: boolean
    lead?: string
    failed?: number
    stale?: number
    count?: number
    jobs?: {
      name: string
      tool: string
      target: string
      chip: { text: string; tone: string }
      tone: string
      icon: string
      last_run: string
      last_run_ago: string
      last_ok: string
      last_ok_ago: string
      last_ok_empty: boolean
      unknown: boolean
      schedule: string
      detail: string
      updated: string
    }[]
  }
  certificates: {
    allowed: boolean
    lead?: string
    expired?: number
    soon?: number
    count?: number
    certs?: {
      subject: string
      issuer: string
      self_signed: boolean
      is_ca: boolean
      key_type: string
      chip: { text: string; tone: string }
      tone: string
      icon: string
      not_before: string
      not_after: string
      sans: string[]
      sans_more: number
      sources: string[]
      fingerprint: string
      updated: string
    }[]
  }
  server_hardware: {
    allowed: boolean
    has?: boolean
    source?: string
    updated?: string
    failed_psu?: number
    bad_sensors?: number
    sensors_critical?: number
    sensor_count?: number
    psu_count?: number
    inlet?: string | null
    inlet_tone?: string | null
    inlet_label?: string
    psus?: { name: string; ok: boolean; status: string; watts: number }[]
    ecc?: {
      window: string
      source: string
      corrected: number
      uncorrectable: number
      dimms: { label: string; corrected: number; uncorrectable: number }[]
    } | null
    groups?: {
      type: string
      label: string
      icon: string
      bad: boolean
      warn: boolean
      rows: { name: string; status: string; reading: string }[]
    }[]
  }
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceExternalMonitoringPage {
  device: { id: number }
  tabs: ExternalMonitoringTab[]
}


/* union from /tmp/v4-inventoree-card/v4/web/src/api/devices.ts */
export interface DeviceInitialEventsPage {
  device: { id: number }
  groups: InitialEventGroup[]
  plain: string[]
}


/* union from /tmp/v4-inventoree-hard/v4/web/src/api/devices.ts */
export interface DeviceLicenseRow {
  DT_RowId: string
  ID: number
  application_id: number
  short_name: string
  total: string
  active: string
  device_status: string
  actions: string
}


/* union from /tmp/v4-inventoree-hard/v4/web/src/api/devices.ts */
export interface DeviceLicensesTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: DeviceLicenseRow[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceLogin {
  user: string
  type: string
  at: string
  open: boolean
  seconds: number
  duration: string
  left_at: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceNeighborWatchPage {
  available: boolean
  device: { id: number }
  watches: NeighborWatchRow[]
  candidates: NeighborCandidate[]
  counts: { up: number; down: number; unknown: number }
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceNetworkStatusPage {
  device: { id: number }
  has: boolean
  counts: NetworkCounts
  ports: NetworkPortCard[]
  peers: NetworkPeer[]
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export interface DeviceOpteemumEventsPage {
  device: { id: number }
  categories: Record<string, string>
  active: string
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export interface DeviceOsPage {
  device: { id: number }
  os: {
    name: string
    version: string
    install_date: string
    license: string
    last_boot: string
    auto_updates: number | null
    family: string
    role: string
  }
  missing_count: number
  /** Still not installed, including status=1 (Pending..). Drives the table vs up-to-date banner. */
  open_count: number
  is_linux: boolean
  auto_update_on: boolean
  tiles: OsTile[]
  facts: OsFact[]
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export interface DeviceOsPatchesPage {
  device: { id: number }
  tabs: OsPatchTab[]
  active: OsPatchKind
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DevicePerformanceManagementPage {
  device: { id: number }
  live_socket: boolean
  is_server: boolean
  tabs: PerformanceTab[]
  service: PerformanceServiceCard | null
  history: PerformanceHistory
  trend_metrics: { key: string; label: string }[]
  charts: PerformanceChartMeta[]
  live: ProactiviteeLivePayload & {
    source?: string
    cpu_user?: number | null
    cpu_system?: number | null
    cpu_psi?: number | null
    cpu_speed_limit?: number | null
    cpu_throttle_reason?: string
    ram_mix?: PerformanceRamMix | null
    ram_psi?: number | null
  }
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceProactiviteeEventsPage {
  device: { id: number }
  categories: Record<string, string>
  active: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceProcessLoadPage {
  device: { id: number }
  processes: ProcessLoadRow[]
  chart: ChartPoint[]
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export interface DevicePropertiesForm {
  brandId: string
  serial: string
  inventory: string
  acquisition: string
  warrantyExpiry: string
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export interface DevicePropertiesPage {
  device: { id: number }
  form: DevicePropertiesForm
  brands: NamedId[]
}


/* union from /tmp/v4-drop-roles/v4/web/src/api/devices.ts */
export interface DeviceQuickDetailsPage {
  device: { id: number; type: string | null; brand: string | null; model: string | null }
  age_years: number
  age_label: string
  age_old: boolean
  status: number | null
  cpu: string | null
  ram: string | null
  storage: string | null
  serial: string | null
  events: { date: string; message: string; user: string }[]
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export interface DeviceRemoteEventsPage {
  device: { id: number }
  archived_count: number
  empty: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceRemoteOpsPage {
  device: { id: number; name: string; role: string; os: string }
  show_test_connect: boolean
  groups: RemoteOpsGroup[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceSession {
  id: string
  user_name: string
  domain: string
  sid: string
  sid_label: string
  login_type: string
  logon_id: string
  local_path: string
  local_path_size: string
  roaming_profile: string
  roaming_profile_path: string
  roaming_profile_path_size: string
  logged_off: boolean
  windows_profile: boolean
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceSessionsPage {
  device: { id: number }
  linux: boolean
  sessions: DeviceSession[]
  logins: DeviceLogin[]
  /** `opteemum.log_off_user`: butonul Log off. */
  can_log_off?: boolean
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export interface DeviceSettingsForm {
  ownerId: number
  areaId: number
  officeId: number
  groupId: number
  classified: string
  autoUpdate: boolean
  beta: boolean
  adRelay: boolean
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export interface DeviceSettingsHostType {
  code: string
  label: string
}

export interface DeviceSettingsHost {
  host_type: string
  host_device_id: number
  guests: NamedId[]
  choices: NamedId[]
  types: DeviceSettingsHostType[]
}

export interface DeviceSettingsPage {
  device: { id: number }
  form: DeviceSettingsForm
  owners: NamedId[]
  areas: NamedId[]
  offices: NamedId[]
  groups: NamedId[]
  host: DeviceSettingsHost
}


/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
export interface DeviceSoftseeInstallPage {
  device: { id: number }
  door: SoftseeDoor
  filter_type: 'kits' | 'custom_kits'
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export interface DeviceSoftseePackagesPage {
  device: { id: number }
  empty: string
  jobs: SoftseePackageJob[]
}


/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
export interface DeviceSoftseeUninstallPage {
  device: { id: number }
}


/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
export interface DeviceSoftseeUpdatesPage {
  device: { id: number }
  door: SoftseeDoor
}


/* union from /tmp/v4-inventoree-ui-wire/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Software licenses pe fisa - /api/v2/devices/{id}/software-licenses
 * ------------------------------------------------------------------------- */

export interface DeviceSoftwareLicenseRow {
  DT_RowId: string
  ID: number
  application_id: number
  short_name: string
  total: string
  active: string
  device_status: string
  actions: string
}


/* union from /tmp/v4-inventoree-ui-wire/v4/web/src/api/devices.ts */
export interface DeviceSoftwareLicensesTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: DeviceSoftwareLicenseRow[]
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export interface DeviceStartupPage {
  device: { id: number }
  is_linux: boolean
  services_live: boolean
  running: number
  stopped: number
  enabled: number
  default_pane: 'local' | 'daemons' | 'all'
  groups: {
    local: StartupServiceRow[]
    daemons: StartupServiceRow[]
    all: StartupServiceRow[]
  }
  counts: { local: number; daemons: number; all: number }
  applications: StartupAppRow[]
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export interface DeviceSubSettingsPage {
  device: { id: number }
  subCategory: number
  options: NamedId[]
  empty: boolean
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export interface DeviceSystemAlarmsList {
  alarms: SystemAlarmOpen[]
  responses: Record<string, SystemAlarmResponse>
  readError?: boolean
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export interface DeviceSystemAlarmsPage {
  device: { id: number }
  alarm_counts: Record<string, number>
  alarm_history: Array<{
    type_name: string
    severity: number
    opened_at: string
    closed_at: string
    closed_unix?: number
    duration: number
    note: string
    value_string: string
    hardware_identifier?: string
    closed_by_name?: string
    closed_by?: string
  }>
  open_alarms: Array<{
    ID: number
    severity: number
    value_numeric: string | number
    value_string: string
    created: string
    created_day: string
    created_time: string
    type: number
    hardware_identifier: string
    sign: string
    dinamic: number
    convert: string
    type_name: string
  }>
  coverage: {
    layer: { label: string; watching: boolean }
    rows: SystemAlarmRow[]
    silent: number | null
    os: string
  }
  coverage_error: boolean
  coverage_problems: string[]
  coverage_problem_details: Array<{ step: string; what: string }>
  board_home: string
  quiet_window: { id: number; name: string; scope_type: number; describe: string } | null
  watches: Array<{ kind: string; name: string; port: number }>
  watch_candidates: {
    services: Array<{ name: string; label: string }>
    ports: Array<{ port: number; name: string; label: string }>
  }
  can_edit_alarms: boolean
  global_admin: boolean
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface DeviceSystemPerformancePage {
  device: { id: number }
  performance: { tiles: ProactiviteePerfTile[] } | null
  live: ProactiviteeLivePayload
  thresholds: Record<string, number>
  saturation_pairs: { metric: string; warn: string; crit: string }[]
  cpus: SystemPerformanceCpu[]
  hdds: SystemPerformanceHdd[]
  voltages: SystemPerformanceSensor[]
  fans: SystemPerformanceSensor[]
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export interface DeviceUploadPage {
  device: { id: number }
  keys: string[]
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export interface DeviceWatchPage {
  device: { id: number; tip: number; virtual: number; so: string; os_type: number }
  settings: WatchSettings | null
  servers: WatchOption[]
  communities: WatchOption[]
  gateway_devices: WatchOption[]
  hypervisors: WatchOption[]
  device_hypervisors: number[]
  monitored_devices: WatchOption[]
  device_redundancy: number[]
  core_apps: WatchOption[]
  core_apps_from_sp: boolean
  device_core_apps: number[]
  device_services: WatchServiceRow[]
  template_services: WatchServiceRow[]
  default_services: WatchServiceRow[]
  services_list: { service_id: string; service_name: string }[]
  /** deviceIntervals + deviceAlertTime din snmpSettings: modalul de pe masina. */
  monitoring: WatchMonitoring
}

/**
 * Modalul "Monitoring settings" (deviceMonitoringSettingsModal.php /
 * serviceMonitoringSettings.php): ferestrele in care NU se monitorizeaza
 * (Day / Start time / End time) si dupa cate minute se da alarma.
 */
export interface WatchInterval {
  day: string
  start_time: string
  end_time: string
}

export interface WatchMonitoring {
  intervals: WatchInterval[]
  alert_time: number | null
}


/* union from /tmp/v4-drop-roles/v4/web/src/api/devices.ts */
export interface EndpointIpRow {
  id: number
  device_id: number
  ip: string
  port: number
  max_value: number
  title: string
}


/* union from /tmp/v4-drop-roles/v4/web/src/api/devices.ts */
export interface EndpointPerformancePage {
  device: { id: number }
  settings: Record<string, { min_value: number; max_value: number }>
  connections: { connection_network: number; connection_internet: number; response_time: number }
  ips: EndpointIpRow[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface EventViewerRow {
  DT_RowId: string
  source: string
  category: string
  log_id: string
  count: string
  last_seen: string
  message: string
  occurrences: number
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface EventViewerTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  view: 'live' | 'archived'
  archivedCount: number
  data: EventViewerRow[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Agentbased Extended Monitoring - /api/v2/devices/{id}/extended-monitoring
 * ------------------------------------------------------------------------- */

export interface ExtendedTab {
  slug: string
  href: string
  label: string
  icon: string
  allowed: boolean
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ExternalHistoryRow {
  DT_RowId: string
  type: string
  details: string
  info: string
  outage: string
  up: string
  recover: string
  duration: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ExternalHistoryTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: ExternalHistoryRow[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ExternalMonitoringStatus {
  probe: string
  watchedIp: string
  services: ExternalServiceStatus[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * External Monitoring - /api/v2/devices/{id}/external-monitoring
 * ------------------------------------------------------------------------- */

export interface ExternalMonitoringTab {
  slug: 'status' | 'watch' | 'history'
  label: string
  icon: string
  moved: boolean
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ExternalServiceStatus {
  name: string
  status: string
  icon: string
  title: string
  changed: string
  outage_date: string
  outage_time: string
}


/* union from /tmp/v4-inventoree-card/v4/web/src/api/devices.ts */
export interface InitialEventGroup {
  type: string
  count: number
  items: InitialEventItem[]
}


/* union from /tmp/v4-inventoree-card/v4/web/src/api/devices.ts */
export interface InitialEventItem {
  name: string
  critical: boolean
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface MonitoringCharts {
  categories: string[]
  dates: Record<string, MonitoringSeries[]>
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface MonitoringSeries {
  name: string
  data: number[]
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Scrierile Inventoree de pe fisa: properties / settings / sub-settings /
 * retrieve-logs / upload / depository / agentless. Campurile POST sunt
 * aceleasi ca in Inventoree.php.
 * ------------------------------------------------------------------------- */

export interface NamedId {
  id: number
  name: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface NeighborCandidate {
  id: number
  name: string
  managed: boolean
  type_name: string
  ip: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Neighbor Watch - /api/v2/devices/{id}/neighbor-watch
 * ------------------------------------------------------------------------- */

export interface NeighborWatchRow {
  id: number
  target_id: number
  name: string
  managed: boolean
  type_name: string
  ip: string
  status: string
  rtt_ms: number | null
  last_checked: string | null
  last_error: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface NetworkChartPayload {
  categories: string[]
  series?: { name: string; data: number[] }[]
  dates?: Record<string, { name: string; data: number[] }[]>
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface NetworkCounts {
  has: boolean
  total: number
  inbound: number
  outbound: number
  established: number
  other: number
  age_seconds: number | null
  peers?: NetworkPeer[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Network Monitoring - /api/v2/devices/{id}/network-status
 * ------------------------------------------------------------------------- */

export interface NetworkPeer {
  ip: string
  connections: number
  port: number
  dir: 'in' | 'out' | string
  name: string
  device_id: number
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface NetworkPortCard {
  port: number
  connections: number
  connections_label: string
  addresses: string[]
  process: string
  service: string
  unknown: boolean
  peers: NetworkPeer[]
  unlisted: number
  unlisted_text: string
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Opteemum Events - /api/v2/devices/{id}/opteemum-events
 * ------------------------------------------------------------------------- */

export type OpteemumEventCategory = '' | 'software' | 'actions' | 'alarms' | 'agent'


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export interface OpteemumEventRow {
  DT_RowId: string
  date: string
  message: string
  status: string
  user: string
  category: string
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export interface OpteemumEventsTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  counts: Record<string, number>
  data: OpteemumEventRow[]
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export interface OsFact {
  key: string
  value: string
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Opteemum OS Management - /api/v2/devices/{id}/os-patches
 * ------------------------------------------------------------------------- */

export type OsPatchKind = 'missing' | 'installed' | 'hidden'


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export interface OsPatchRow {
  DT_RowId: string
  ID: number
  patchId: string
  cells: string[]
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export interface OsPatchTab {
  key: OsPatchKind
  label: string
  icon: string
}


/**
 * Randul din OS Management (Opteemum::missing/installed/hiddenPatchesData).
 * `OsPatchRow` de mai sus e celalalt ecran - Softsee Operating System, care
 * primeste celulele gata asezate in `cells`. Aici serverul (App\Devices\
 * Opteemum\OsPatches::present) da fiecare celula pe numele ei, ca in
 * MissingPatchesModel / InstalledPatchesModel / HiddenPatchesModel.
 * `action` + `pick` vin numai pe missing, `pick` pe installed, `actions` pe
 * hidden.
 */
export interface OsPatchManagementRow {
  DT_RowId: string
  patchId: string
  name: string
  severity: string
  downloaded: string
  size: string
  require_source: string
  mandatory: string
  beta: string
  reboot: string
  action_status: string
  action?: string
  pick?: string
  actions?: string
}


export interface OsPatchManagementTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  kind: OsPatchKind
  data: OsPatchManagementRow[]
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export interface OsPatchesTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  type: 0 | 1
  linux: boolean
  data: OsPatchRow[]
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Softsee Operating System - /api/v2/devices/{id}/os-updates
 * (Opteemum::osUpdates + missingPatchesData / installedPatchesData)
 * ------------------------------------------------------------------------- */

export interface OsTile {
  number: string
  icon: string
  label: string
  title: string
  note: string
  tone: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface PerformanceChartMeta {
  key: string
  title: string
  unit: string
  kind: string
  color: string
  tab: string
  top: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface PerformanceHistory {
  days: number
  work_hours: number
  average: number
  worst: number
  average_color: string
  worst_color: string
  from_label: string
  bars: PerformanceHistoryBar[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface PerformanceHistoryBar {
  day: string
  hours: number
  score: number
  color: string
  height: number
  title: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface PerformanceRamMix {
  total: number
  wired: number
  app: number
  compressed: number
  cached: number
  free: number
  labels: Record<string, string>
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface PerformanceSeriesLine {
  points: { name: string; value: number }[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface PerformanceServiceCard {
  tone: string
  status_label: string
  role_label: string
  others: string[]
  kpi: { label: string; display: string } | null
  runs: string | null
  version: string | null
  version_title: string
  signals: { label: string; display: string }[]
  next: string | null
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface PerformanceSplitSeries {
  categories: string[]
  dates: Record<string, { name: string; data: number[] }[]>
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ------------------------------------------------------------------------- */
/* Fila Performance Management - /api/v2/devices/{id}/performance-management */
/* ------------------------------------------------------------------------- */

export interface PerformanceTab {
  key: string
  href: string
  label: string
  icon: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface PerformanceTrend {
  range: string
  metric: string
  label: string
  unit: string
  tier: string
  points: PerformanceTrendPoint[]
  extreme: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface PerformanceTrendPoint {
  name: string
  avg: number
  max: number
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Proactivitee Events - /api/v2/devices/{id}/proactivitee-events
 * ------------------------------------------------------------------------- */

export type ProactiviteeEventCategory = '' | 'session' | 'configuration' | 'agent' | 'apps' | 'other'


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ProactiviteeEventRow {
  DT_RowId: string
  date: string
  message: string
  response: string
  user: string
  category: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ProactiviteeEventsTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  counts: Record<string, number>
  data: ProactiviteeEventRow[]
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface ProcessLoadRow {
  resource_name: string
  device_id: number
  cpu_load: number
  cpu_text: string
  memory_load: number
  memory_text: string
  io_per_sec: number
  io_text: string
  created_at: string
  killed: boolean
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export interface RemoteEventRow {
  DT_RowId: string
  title: string
  message: string
  source: string
  category: string
  log_id: string
  occurrences: number
  count: string
  time: string
  first_time: string
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export interface RemoteEventsTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  view: RemoteEventsView
  data: RemoteEventRow[]
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Remote Event Viewer - /api/v2/devices/{id}/remote-events
 * ------------------------------------------------------------------------- */

export type RemoteEventsView = 'live' | 'archived'


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Remote Operations - /api/v2/devices/{id}/remote-ops
 * ------------------------------------------------------------------------- */

export interface RemoteOpsCommand {
  command: string
  command_title: string
  info: string
  checked: boolean
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface RemoteOpsGroup {
  id: number
  group_name: string
  commands: RemoteOpsCommand[]
}


/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Filele Softsee Install / Update / Uninstall Applications
 * ------------------------------------------------------------------------- */

export type SoftseeDoor = 'softsee' | 'opteemum'


/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
export interface SoftseeHtmlRow {
  DT_RowId: string
  ID: number
  cells: string[]
}


/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
export interface SoftseeHtmlTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: SoftseeHtmlRow[]
  filter_type?: 'kits' | 'custom_kits'
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila Softsee Install Packages - /api/v2/devices/{id}/softsee-packages
 * (Softsee::devicePackages)
 * ------------------------------------------------------------------------- */

export interface SoftseePackageJob {
  id: number
  package: string
  starts: string
  deadline: string
  how: string
  last_state: number | null
  last_label: string
  last_note: string
  last_due: string
  withdrawn: boolean
  schedule: string
  schedule_color: string
  device_count: number
  can_stop: boolean
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export interface StartupAppRow {
  id: number
  name: string
  user: string
  location: string
  command: string
  impact: string
  is_removable: boolean
  recommended: boolean
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Filele Startup Services / Apps - /api/v2/devices/{id}/startup
 * ------------------------------------------------------------------------- */

export interface StartupBadge {
  cls: string
  text: string
  title: string
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export interface StartupServiceRow {
  name: string
  display: string
  description: string
  path: string
  type: string
  enabled: string
  restarts: number | null
  badge: StartupBadge
  can_start: boolean
  restarted: boolean
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
/* ---------------------------------------------------------------------------
 * Fila System Alarms (tabla de acoperire) - /api/v2/devices/{id}/system-alarms
 * (Proactivitee::deviceAlarms / alarmsList + scrierile de pe placa).
 * ------------------------------------------------------------------------- */

export type SystemAlarmFlag = 'all' | '0' | '1' | '2' | '3'


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export interface SystemAlarmForecast {
  label?: string
  rate?: string
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export interface SystemAlarmHistory {
  ID?: number
  severity: number
  type_name: string
  hardware_identifier?: string
  resolved_description?: string
  value_string?: string
  created: string
  resolved?: string
  closed_by_name?: string
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export interface SystemAlarmLayer {
  label: string
  watching: boolean
  source?: string
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export interface SystemAlarmOpen {
  ID: number
  severity: number
  type_name: string
  dinamic: number
  convert: string
  sign: string
  hardware_identifier?: string
  value_numeric?: number | string
  value_string?: string
  created: string
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export interface SystemAlarmQuiet {
  id: number
  name: string
  scope_type: number
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export interface SystemAlarmResponse {
  notified?: number
  ticket_id?: number
  ticket_due_at?: string
  ai_mode?: string
  ai_outcome?: string
  ai_summary?: string
  state?: string
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export interface SystemAlarmRow {
  type: number
  home: string
  home_label: string
  home_module: string
  href: string
  signal: string
  watched: boolean
  layer: string
  threshold: string
  current: string
  forecast: { label?: string; rate?: string; hours?: number } | null
  culprit: string
  state: string
  state_label: string
  open: { severity?: number; value_string?: string } | null
  value_string: string
  toggleable?: boolean
  board?: string
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export interface SystemAlarmWatch {
  kind: string
  name: string
  port: number
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/* ------------------------------------------------------------------------- */
/* Fila System Performance - /api/v2/devices/{id}/system-performance         */
/* ------------------------------------------------------------------------- */

export interface SystemPerformanceCpu {
  name: string
  load: number
  current_clock: string
  max_clock: string
  temperature: string
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface SystemPerformanceHdd {
  index: string
  size: string
  unallocated: string
  smart: 'ok' | 'fail' | 'unsupported'
  wmi_ok: boolean
  temperature: string | null
  used: number
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export interface SystemPerformanceSensor {
  component: string
  current: string
  min: string
  max: string
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export interface WatchOption {
  ID: number
  name?: string
  device_name?: string
  Denumire?: string
  company_name?: string
  application_name?: string
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export interface WatchServiceRow {
  index: number
  service_id: string
  service_name: string
  monitored: number
  values: { field: string; value: string }[]
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export interface WatchSettings {
  server_id: number
  community_id: number
  parent_id: number
  severity: number
  agent_ip: string
  udp_port: string
  autoclose: number
  escalate: number
  gateway: number
  hypervisor: number
  no_keepalive: number
  maintenance: number
  development: number
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function activeAppsPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'active-apps'],
    queryFn: () => apiGet<DeviceActiveAppsPage>(`/devices/${id}/active-apps`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function activeAppsTableQuery(
  id: number,
  params: { start: number; length: number; search: string; orderColumn: number; orderDir: 'asc' | 'desc' },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('search[value]', params.search)
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  return queryOptions({
    queryKey: ['device', id, 'active-apps', 'table', params],
    queryFn: () => apiGet<ActiveAppsTable>(`/devices/${id}/active-apps/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function addServiceWatch(id: number, kind: string, name: string, port: number) {
  /* Capul e `service-watch` (Proactivitee::addServiceWatch). `watch` e comutatorul
     de tip de alarma, alt lucru - vezi setAlarmTypeWatch. */
  return apiPost<{ ok: boolean }>(`/devices/${id}/system-alarms/service-watch`, { kind, name, port })
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function agentlessQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'agentless'],
    queryFn: () => apiGet<DeviceAgentlessPage>(`/devices/${id}/agentless`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export function appManagementPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'app-management'],
    queryFn: () => apiGet<DeviceAppManagementPage>(`/devices/${id}/app-management`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export function appManagementTableQuery(
  id: number,
  params: { start: number; length: number; search: string; applicationsType: string },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('search[value]', params.search)
  q.set('applicationsType', params.applicationsType)
  return queryOptions({
    queryKey: ['device', id, 'app-management', 'table', params],
    queryFn: () => apiGet<AppManagementTable>(`/devices/${id}/app-management/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Softsee Install Packages - /api/v2/devices/{id}/softsee-packages
 * (Softsee::devicePackages)
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-inventoree-hard/v4/web/src/api/devices.ts */
export function cardSettingsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'card-settings'],
    queryFn: () => apiGet<DeviceCardSettingsPage>(`/devices/${id}/card-settings`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function closeRunningApplication(id: number, applicationId: string) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/active-apps/close`, {
    device: id,
    application_id: applicationId,
  })
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export function closeSystemAlarm(id: number, alarm: number, description: string) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/system-alarms/alarm/${alarm}/close`, { alarm, description })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function cmdPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'cmd'],
    queryFn: () => apiGet<DeviceCmdPage>(`/devices/${id}/cmd`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* union from /tmp/v4-inventoree-card/v4/web/src/api/devices.ts */
export function commandResultQuery(deviceId: number, commandId: number) {
  return queryOptions({
    queryKey: ['device', deviceId, 'commands', commandId, 'result'],
    queryFn: () => apiGet<DeviceCommandResult>(`/devices/${deviceId}/commands/${commandId}/result`),
    staleTime: 60_000,
  })
}

/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function configRelayQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'config-relay'],
    queryFn: () => apiGet<DeviceConfigRelayPage>(`/devices/${id}/config-relay`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function coreServicesQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'core-services'],
    queryFn: () => apiGet<DeviceCoreServicesPage>(`/devices/${id}/core-services`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Remote Operations - /api/v2/devices/{id}/remote-ops
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-drop-roles/v4/web/src/api/devices.ts */
export function deviceQuickDetailsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'quick-details'],
    queryFn: () => apiGet<DeviceQuickDetailsPage>(`/devices/${id}/quick-details`),
  })
}

/** Softsee::deviceQuickDetails - fila Software a tichetului. Desenele vin gata facute (HTML/SVG). */
export interface DeviceSoftseeQuickDetailsPage {
  device: { id: number; name: string }
  summary: {
    operating_system: string
    os_label: string
    os_tone: string
    default_browser: string
    office: string
    default_mail_client: string
  } | null
  installed: number
  licensed: number
  suspected: number
  outdated: number
  vulnerable: number
  issues: { product_name: string; version_affected: string; version_value: string; description: string }[]
  cve_state: { tone: string; text: string; hint: string }
  events: { date: string; message: string; user: string }[]
  events_24h: number
  software_chart: string
  events_chart: string
}

export function deviceSoftseeQuickDetailsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'softsee-quick-details'],
    queryFn: () => apiGet<DeviceSoftseeQuickDetailsPage>(`/devices/${id}/softsee-quick-details`),
  })
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export function endSystemMaintenance(id: number, windowId: number) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/system-alarms/maintenance/end`, { id: windowId })
}


/* union from /tmp/v4-drop-roles/v4/web/src/api/devices.ts */
export function endpointPerformanceSettingsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'endpoint-performance-settings'],
    queryFn: () => apiGet<EndpointPerformancePage>(`/devices/${id}/endpoint-performance-settings`),
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function extendedMonitoringQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'extended-monitoring'],
    queryFn: () => apiGet<DeviceExtendedMonitoringPage>(`/devices/${id}/extended-monitoring`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function externalMonitoringHistoryQuery(
  id: number,
  params: { start: number; length: number; orderColumn: number; orderDir: 'asc' | 'desc'; search: string },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  q.set('search[value]', params.search)
  q.set('serviceType', '0')
  return queryOptions({
    queryKey: ['device', id, 'external-monitoring', 'history', params],
    queryFn: () => apiGet<ExternalHistoryTable>(`/devices/${id}/external-monitoring/history?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ------------------------------------------------------------------------- */
/* Filele Proactivitee: Active Applications / Process Load / Sessions        */
/* ------------------------------------------------------------------------- */


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function externalMonitoringPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'external-monitoring'],
    queryFn: () => apiGet<DeviceExternalMonitoringPage>(`/devices/${id}/external-monitoring`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function externalMonitoringStatusQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'external-monitoring', 'status'],
    queryFn: () => apiGet<ExternalMonitoringStatus>(`/devices/${id}/external-monitoring/status`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function hibernateDevice(id: number) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/remote-ops/hibernate`, { device: id })
}


/* union from /tmp/v4-inventoree-ui-wire/v4/web/src/api/devices.ts */
export function initialEventsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'initial-events'],
    queryFn: () => apiGet<{ events: { message: string }[] }>(`/devices/${id}/initial-events`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function killProcess(id: number, processName: string) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/extended-monitoring/kill`, { process_name: processName })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/** Lock screen (Respond): comanda LCK, toate sesiunile aparatului. */
export function lockDeviceScreen(id: number) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/sessions/lock`, {})
}

export function logOffSession(id: number, username: string, sessionId: string) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/sessions/log-off`, {
    username,
    session_id: sessionId,
  })
}

export function logOffExtendedMonitoring(id: number, username: string, sessionId: string) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/extended-monitoring/log-off`, {
    username,
    session_id: sessionId,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Network Monitoring - /api/v2/devices/{id}/network-status
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function moveFromDepository(id: number) {
  return apiPost<{ status: 'OK' }>(`/devices/${id}/depository/restore`, { device: id })
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function moveToDepository(id: number) {
  return apiPost<{ status: 'OK' }>(`/devices/${id}/depository`, { device: id })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function neighborWatchAdd(id: number, target: number) {
  return apiPost<{ ok: boolean; error?: string }>(`/devices/${id}/neighbor-watch/add`, { target })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function neighborWatchCheck(id: number) {
  return apiPost<{ ok: boolean; error?: string }>(`/devices/${id}/neighbor-watch/check`, {})
}

/* ---------------------------------------------------------------------------
 * Fila Core Services - /api/v2/devices/{id}/core-services
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function neighborWatchQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'neighbor-watch'],
    queryFn: () => apiGet<DeviceNeighborWatchPage>(`/devices/${id}/neighbor-watch`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function neighborWatchRemove(id: number, watch: number) {
  return apiPost<{ ok: boolean; error?: string }>(`/devices/${id}/neighbor-watch/remove`, { watch })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function networkConnectionsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'network-status', 'connections'],
    queryFn: () => apiGet<NetworkChartPayload>(`/devices/${id}/network-status/connections`),
    staleTime: 30_000,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function networkCountsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'network-status', 'counts'],
    queryFn: () => apiGet<NetworkCounts>(`/devices/${id}/network-status/counts`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: 10_000,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function networkLatencyQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'network-status', 'latency'],
    queryFn: () => apiGet<NetworkChartPayload>(`/devices/${id}/network-status/latency`),
    staleTime: 30_000,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function networkStatisticsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'network-status', 'statistics'],
    queryFn: () => apiGet<NetworkChartPayload>(`/devices/${id}/network-status/statistics`),
    staleTime: 30_000,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function networkStatusQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'network-status'],
    queryFn: () => apiGet<DeviceNetworkStatusPage>(`/devices/${id}/network-status`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function networkWatch(id: number) {
  return apiPost<{ success: boolean; ttl: number }>(`/devices/${id}/network-status/watch`, {})
}

/* ---------------------------------------------------------------------------
 * Fila Neighbor Watch - /api/v2/devices/{id}/neighbor-watch
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export function opteemumEventsPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'opteemum-events'],
    queryFn: () => apiGet<DeviceOpteemumEventsPage>(`/devices/${id}/opteemum-events`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export function opteemumEventsTableQuery(
  id: number,
  params: {
    start: number
    length: number
    orderColumn: number
    orderDir: 'asc' | 'desc'
    search: string
    category: OpteemumEventCategory
    from: string
    to: string
  },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  q.set('search[value]', params.search)
  q.set('category', params.category)
  q.set('from', params.from)
  q.set('to', params.to)
  return queryOptions({
    queryKey: ['device', id, 'opteemum-events', 'table', params],
    queryFn: () => apiGet<OpteemumEventsTable>(`/devices/${id}/opteemum-events/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export function osPatchesPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'os-patches'],
    queryFn: () => apiGet<DeviceOsPatchesPage>(`/devices/${id}/os-patches`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export function osPatchesTableQuery(
  id: number,
  params: {
    kind: OsPatchKind
    start: number
    length: number
    orderColumn: number
    orderDir: 'asc' | 'desc'
    search: string
  },
) {
  const q = new URLSearchParams()
  q.set('kind', params.kind)
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  q.set('search[value]', params.search)
  return queryOptions({
    queryKey: ['device', id, 'os-patches', 'table', params],
    queryFn: () => apiGet<OsPatchManagementTable>(`/devices/${id}/os-patches/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Filele Startup Services / Apps - /api/v2/devices/{id}/startup
 * ------------------------------------------------------------------------- */


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export function osUpdatesPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'os-updates'],
    queryFn: () => apiGet<DeviceOsPage>(`/devices/${id}/os-updates`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export function osUpdatesTableQuery(
  id: number,
  params: { type: 0 | 1; start: number; length: number; search: string },
) {
  const q = new URLSearchParams()
  q.set('type', String(params.type))
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('search[value]', params.search)
  return queryOptions({
    queryKey: ['device', id, 'os-updates', 'table', params],
    queryFn: () => apiGet<OsPatchesTable>(`/devices/${id}/os-updates/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Softsee Application Management - /api/v2/devices/{id}/app-management
 * (Opteemum::applicationManagement + deviceApplications +
 * installedApplicationsData)
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function performanceManagementLiveQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'performance-management', 'live'],
    queryFn: () => apiGet<DevicePerformanceManagementPage['live']>(`/devices/${id}/performance-management/live`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: 1000,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function performanceManagementQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'performance-management'],
    queryFn: () => apiGet<DevicePerformanceManagementPage>(`/devices/${id}/performance-management`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
/** Opteemum::performanceKick - SPD start rearmat cat un ecran live sta deschis (numai Windows). */
export function performanceManagementKick(id: number) {
  return apiPost<{ success: boolean; kicked: boolean }>(`/devices/${id}/performance-management/kick`, {})
}

/** Opteemum::liveDomain - emailul proprietarului, din care statia scoate domeniul de verificat. */
export function performanceDomainQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'performance-management', 'domain'],
    queryFn: () => apiGet<{ email: string; live_socket: boolean }>(`/devices/${id}/performance-management/domain`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export interface DriverErrorRow {
  DT_RowId: string
  ID: number
  name: string
  hardware_id: string
  date: string
  error: string
}

export interface DriverErrorsTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: DriverErrorRow[]
}

/** Opteemum::remoteDriverErrors + Proactivitee::getDriverIssuesData / driverIssueDetail. */
export function driverErrorsTableQuery(id: number, r: { start: number; length: number; search: string; orderColumn: number; orderDir: 'asc' | 'desc' }) {
  return queryOptions({
    queryKey: ['device', id, 'driver-errors', r],
    queryFn: () => {
      const p = new URLSearchParams({
        draw: '1',
        start: String(r.start),
        length: String(r.length),
        'search[value]': r.search,
        'order[0][column]': String(r.orderColumn),
        'order[0][dir]': r.orderDir,
      })
      return apiGet<DriverErrorsTable>(`/devices/${id}/driver-errors/table?${p.toString()}`)
    },
  })
}

/**
 * Opteemum::deviceReport - PDF-ul „Device Report" (Export Report din Opteemum
 * Summary). Portalul il scria in tmp/opteemum si il dadea cu force_download;
 * aici vine direct in raspuns si se salveaza in browser.
 */
export async function downloadDeviceReport(id: number): Promise<void> {
  const out = await apiExchange(`/devices/${id}/opteemum-report.pdf`, { method: 'GET' })
  if (!out.type.includes('pdf')) {
    let message = 'Report could no be generated'
    try {
      message = (JSON.parse(out.text) as { message?: string }).message ?? message
    } catch {
      /* Raspunsul nu era JSON; ramane propozitia de mai sus. */
    }
    throw new ApiError(out.status || 500, message)
  }
  saveBytes(out.bytes, out.type, out.filename, `DeviceReport_${id}.pdf`)
}

export function performanceManagementWatch(id: number) {
  return apiPost<{ success: boolean; ttl: number }>(`/devices/${id}/performance-management/watch`, {})
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function performanceSeriesQuery(id: number, kind: 'load' | 'memory' | 'apps' | 'discio' | 'network') {
  return queryOptions({
    queryKey: ['device', id, 'performance-management', 'series', kind],
    queryFn: () => apiGet<PerformanceSeriesLine & PerformanceSplitSeries>(`/devices/${id}/performance-management/series/${kind}`),
    staleTime: 30_000,
    gcTime: 60_000,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function performanceTopQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'performance-management', 'top'],
    queryFn: () => apiGet<Record<string, { name: string; value: number }>>(`/devices/${id}/performance-management/top`),
    staleTime: 15_000,
    gcTime: 30_000,
    refetchOnWindowFocus: false,
    refetchInterval: 15_000,
  })
}

/* ------------------------------------------------------------------------- */
/* Fila Configuration - /api/v2/devices/{id}/configuration                   */
/* (perechea lui inventoree/deviceDetails + updateHostRelationship)          */
/* ------------------------------------------------------------------------- */


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function performanceTrendQuery(id: number, range: string, metric: string) {
  return queryOptions({
    queryKey: ['device', id, 'performance-management', 'trend', range, metric],
    queryFn: () =>
      apiGet<PerformanceTrend>(`/devices/${id}/performance-management/trend?range=${encodeURIComponent(range)}&metric=${encodeURIComponent(metric)}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function proactiviteeEventViewerPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'proactivitee-event-viewer'],
    queryFn: () => apiGet<DeviceEventViewerPage>(`/devices/${id}/proactivitee-event-viewer`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function proactiviteeEventViewerTableQuery(
  id: number,
  params: {
    start: number
    length: number
    orderColumn: number
    orderDir: 'asc' | 'desc'
    search: string
    view: 'live' | 'archived'
  },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  q.set('search[value]', params.search)
  q.set('view', params.view)
  return queryOptions({
    queryKey: ['device', id, 'proactivitee-event-viewer', 'table', params],
    queryFn: () => apiGet<EventViewerTable>(`/devices/${id}/proactivitee-event-viewer/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * External Monitoring - /api/v2/devices/{id}/external-monitoring
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function proactiviteeEventsPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'proactivitee-events'],
    queryFn: () => apiGet<DeviceProactiviteeEventsPage>(`/devices/${id}/proactivitee-events`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function proactiviteeEventsTableQuery(
  id: number,
  params: {
    start: number
    length: number
    orderColumn: number
    orderDir: 'asc' | 'desc'
    search: string
    category: ProactiviteeEventCategory
    from: string
    to: string
  },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  q.set('search[value]', params.search)
  q.set('category', params.category)
  q.set('from', params.from)
  q.set('to', params.to)
  return queryOptions({
    queryKey: ['device', id, 'proactivitee-events', 'table', params],
    queryFn: () => apiGet<ProactiviteeEventsTable>(`/devices/${id}/proactivitee-events/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Remote Event Viewer - /api/v2/devices/{id}/proactivitee-event-viewer
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function processLoadPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'process-load'],
    queryFn: () => apiGet<DeviceProcessLoadPage>(`/devices/${id}/process-load`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function propertiesQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'properties'],
    queryFn: () => apiGet<DevicePropertiesPage>(`/devices/${id}/properties`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function remoteCommands(id: number, groupName: string, groupId: number, actions: string[], inform: boolean) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/remote-ops/commands`, {
    device: id,
    commands_group: groupName,
    commands_group_id: groupId,
    actions,
    ...(inform ? { inform_user: 1 } : {}),
  })
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export function remoteEventsPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'remote-events'],
    queryFn: () => apiGet<DeviceRemoteEventsPage>(`/devices/${id}/remote-events`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export function remoteEventsTableQuery(
  id: number,
  params: {
    view: RemoteEventsView
    start: number
    length: number
    orderColumn: number
    orderDir: 'asc' | 'desc'
    search: string
  },
) {
  const q = new URLSearchParams()
  q.set('view', params.view)
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  q.set('search[value]', params.search)
  return queryOptions({
    queryKey: ['device', id, 'remote-events', 'table', params],
    queryFn: () => apiGet<RemoteEventsTable>(`/devices/${id}/remote-events/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Opteemum Events - /api/v2/devices/{id}/opteemum-events
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function remoteOpsPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'remote-ops'],
    queryFn: () => apiGet<DeviceRemoteOpsPage>(`/devices/${id}/remote-ops`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Scoica Remote Command Prompt - /api/v2/devices/{id}/cmd
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function removeServiceWatch(id: number, kind: string, name: string, port: number) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/system-alarms/service-watch/remove`, { kind, name, port })
}

/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function restartDevice(id: number, message: string, time: string, force: boolean) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/remote-ops/restart`, {
    deviceId: id,
    restart_message: message,
    restart_time: time,
    ...(force ? { force_restart: 1 } : {}),
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function restartService(id: number, serviceName: string) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/extended-monitoring/restart`, { service_name: serviceName })
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
/**
 * POST catre un cap care raspunde ca CI3: greseala din formular vine inapoi ca
 * TEXT pe 401 (`Field "device_office" is required`, `Email address invalid`),
 * nu ca sesiune picata - sesiunea picata e 401 JSON, cu `unauthenticated`.
 * `apiPost` nu face diferenta si ar trimite omul la login pentru o greseala de
 * tipar, deci capetele astea trec pe aici.
 */
async function postCi3Form<T>(path: string, fields: Record<string, PostValue>): Promise<T> {
  const response = await fetch(`/api/v2${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json, text/plain', 'Content-Type': 'application/x-www-form-urlencoded' },
    body: encodePost(fields),
  })
  if (!response.ok) {
    const text = await response.text()
    let message = ''
    let session = false
    try {
      const body = JSON.parse(text) as { error?: string; message?: string }
      message = body.message ?? ''
      session = body.error === 'unauthenticated'
    } catch {
      message = text.trim()
    }
    if (response.status === 401 && session) {
      sendToLogin()
      throw new ApiError(401, 'Not logged in')
    }
    throw new ApiError(response.status, message !== '' ? message : `${response.status} ${response.statusText}`)
  }

  return (await response.json()) as T
}

/**
 * Retrieve logs de pe fila Settings (Inventoree::retrieveLogsRequest).
 * Adresa gresita vine ca text pe 401 - toast, nu login, ca in portalul de acum.
 */
export function retrieveLogs(id: number, email: string) {
  return postCi3Form<{ status: 'OK' }>(`/devices/${id}/retrieve-logs`, { device_unique: id, logs_email: email })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function runConfigRelay(
  id: number,
  fields: { target_id: number; credential_id: number; command: string; host: string; port: number },
) {
  return apiPost<ConfigRelayQueueResult>(`/devices/${id}/config-relay`, fields)
}

/* ---------------------------------------------------------------------------
 * Fila Agentbased Extended Monitoring - /api/v2/devices/{id}/extended-monitoring
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function saveAgentless(id: number, fields: Record<string, string>) {
  return apiPost<{ status: 'OK' }>(`/devices/${id}/agentless`, fields)
}

/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function saveProperties(id: number, fields: Record<string, string>) {
  return apiPost<{ status: 'OK' }>(`/devices/${id}/properties`, fields)
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function saveSettings(id: number, fields: Record<string, string>) {
  return postCi3Form<{ status: 'OK' }>(`/devices/${id}/settings`, fields)
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function saveSubSettings(id: number, subCategory: number) {
  return apiGet<{ status: string; message: string }>(
    `/devices/${id}/sub-settings?deviceID=${id}&subCategory=${subCategory}`,
  )
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function saveWatchServices(id: number, fields: Record<string, string | number | Array<string | number>>) {
  return apiPost<DeviceWatchPage>(`/devices/${id}/watch/services`, fields)
}

/** getServiceMonitoringSettings (cu `service`) sau intervalele masinii (fara). */
export function watchMonitoringQuery(id: number, service: number | null) {
  return queryOptions({
    queryKey: ['device', id, 'watch', 'monitoring', service ?? 0],
    queryFn: () => apiGet<WatchMonitoring>(`/devices/${id}/watch/monitoring${service ? `?service=${service}` : ''}`),
  })
}

/** save(Device|Service)MonitoringIntervals: day[] / start_time[] / end_time[]. */
export function saveWatchIntervals(id: number, service: number | null, rows: { day: string; start_time: string; end_time: string }[]) {
  return apiPost<WatchMonitoring>(`/devices/${id}/watch/monitoring/intervals`, {
    device: id,
    service: service ?? '',
    day: rows.map((r) => r.day),
    start_time: rows.map((r) => r.start_time),
    end_time: rows.map((r) => r.end_time),
  })
}

/** reset(Device|Service)MonitoringIntervals. */
export function resetWatchIntervals(id: number, service: number | null) {
  return apiPost<WatchMonitoring>(`/devices/${id}/watch/monitoring/intervals/reset`, { device: id, service: service ?? '' })
}

/** save(Device|Service)AlertTime; gol = se sterge. */
export function saveWatchAlertTime(id: number, service: number | null, alertTime: string) {
  return apiPost<WatchMonitoring>(`/devices/${id}/watch/monitoring/alert-time`, { device: id, service: service ?? '', alert_time: alertTime })
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function saveWatchSettings(id: number, fields: Record<string, string | number | Array<string | number>>) {
  return apiPost<DeviceWatchPage>(`/devices/${id}/watch`, fields)
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function sendDeviceMessage(id: number, message: string) {
  return apiPost<{ status: string }>(`/devices/${id}/remote-ops/message`, { deviceId: id, message })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function sessionsPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'sessions'],
    queryFn: () => apiGet<DeviceSessionsPage>(`/devices/${id}/sessions`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Configuration Relay - /api/v2/devices/{id}/config-relay
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function setAlarmTypeWatch(id: number, type: number, enabled: boolean) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/system-alarms/watch`, {
    type,
    enabled: enabled ? 1 : 0,
  })
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export function setSystemAlarmWatch(id: number, type: number, enabled: number) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/system-alarms/watch`, { type, enabled })
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function settingsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'inventoree-settings'],
    queryFn: () => apiGet<DeviceSettingsPage>(`/devices/${id}/settings`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function shutdownDevice(id: number, message: string, time: string, force: boolean) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/remote-ops/shutdown`, {
    deviceId: id,
    shutdown_message: message,
    shutdown_time: time,
    ...(force ? { force_shutdown: 1 } : {}),
  })
}


/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
export function softseeInstallPageQuery(id: number, door: SoftseeDoor) {
  return queryOptions({
    queryKey: ['device', id, `${door}-install`],
    queryFn: () => apiGet<DeviceSoftseeInstallPage>(`/devices/${id}/${door}-install`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
export function softseeInstallTableQuery(
  id: number,
  door: SoftseeDoor,
  params: { start: number; length: number; search: string; filterType: 'kits' | 'custom_kits' },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('search[value]', params.search)
  q.set('filterType', params.filterType)
  return queryOptions({
    queryKey: ['device', id, `${door}-install`, 'table', params],
    queryFn: () => apiGet<SoftseeHtmlTable>(`/devices/${id}/${door}-install/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: 20_000,
  })
}


/* union from /tmp/wt-v4-softsee-rest/v4/web/src/api/devices.ts */
export function softseePackagesPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'softsee-packages'],
    queryFn: () => apiGet<DeviceSoftseePackagesPage>(`/devices/${id}/softsee-packages`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
export function softseeUninstallPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'softsee-uninstall'],
    queryFn: () => apiGet<DeviceSoftseeUninstallPage>(`/devices/${id}/softsee-uninstall`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
export function softseeUninstallTableQuery(
  id: number,
  params: { start: number; length: number; search: string },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('search[value]', params.search)
  return queryOptions({
    queryKey: ['device', id, 'softsee-uninstall', 'table', params],
    queryFn: () => apiGet<SoftseeHtmlTable>(`/devices/${id}/softsee-uninstall/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
export function softseeUpdatesPageQuery(id: number, door: SoftseeDoor) {
  return queryOptions({
    queryKey: ['device', id, `${door}-updates`],
    queryFn: () => apiGet<DeviceSoftseeUpdatesPage>(`/devices/${id}/${door}-updates`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/wt-v4-softsee-next/v4/web/src/api/devices.ts */
export function softseeUpdatesTableQuery(
  id: number,
  door: SoftseeDoor,
  params: { start: number; length: number; search: string },
) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('search[value]', params.search)
  return queryOptions({
    queryKey: ['device', id, `${door}-updates`, 'table', params],
    queryFn: () => apiGet<SoftseeHtmlTable>(`/devices/${id}/${door}-updates/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-inventoree-ui-wire/v4/web/src/api/devices.ts */
export function softwareLicensesPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'software-licenses'],
    queryFn: () => apiGet<{ device: { id: number } }>(`/devices/${id}/software-licenses`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-inventoree-ui-wire/v4/web/src/api/devices.ts */
/** Inventoree::getDeviceLicenseForm - licentele aplicatiei, cu cine le are si cate au ramas. */
export interface DeviceLicenseOption {
  id: number
  type_name: string
  licenses_number: number
  devices: number[]
  licenses_left: number
}

export interface DeviceLicenseForm {
  device: number
  app: number
  licenses: DeviceLicenseOption[]
}

export function deviceLicenseFormQuery(id: number, app: number) {
  return queryOptions({
    queryKey: ['device', id, 'licenses', app],
    queryFn: () => apiGet<DeviceLicenseForm>(`/devices/${id}/licenses?app=${app}`),
    staleTime: 0,
    gcTime: 0,
  })
}

/** Inventoree::saveDeviceSoftwareLicenses - `licenses[]`, lista goala scoate tot. */
export function saveDeviceLicenses(id: number, app: number, licenses: number[]) {
  return apiPost<{ status: string }>(`/devices/${id}/licenses`, { app, licenses })
}

export function softwareLicensesTableQuery(id: number, params: { start: number; length: number; orderColumn: number; orderDir: 'asc' | 'desc' }) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  return queryOptions({
    queryKey: ['device', id, 'software-licenses', 'table', params],
    queryFn: () => apiGet<DeviceSoftwareLicensesTable>(`/devices/${id}/software-licenses/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Hardware Changes - /api/v2/devices/{id}/hardware-events
 * (Inventoree::deviceEvents / getDeviceEvents).
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export function startSystemMaintenance(id: number, fields: { hours: number | string; mode: number | string; pass: number | string; name: string; digest_to?: string }) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/system-alarms/maintenance`, {
    hours: fields.hours,
    mode: fields.mode,
    pass: fields.pass,
    name: fields.name,
    digest_to: fields.digest_to ?? '',
  })
}


/* union from /tmp/v4-opteemum-os/v4/web/src/api/devices.ts */
export function startupQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'startup'],
    queryFn: () => apiGet<DeviceStartupPage>(`/devices/${id}/startup`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Fila Remote Event Viewer - /api/v2/devices/{id}/remote-events
 * ------------------------------------------------------------------------- */


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function subSettingsQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'sub-settings'],
    queryFn: () => apiGet<DeviceSubSettingsPage>(`/devices/${id}/sub-settings`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export function systemAlarmsListQuery(id: number, flag: SystemAlarmFlag) {
  return queryOptions({
    queryKey: ['device', id, 'system-alarms', 'list', flag],
    queryFn: () => apiGet<DeviceSystemAlarmsList>(`/devices/${id}/system-alarms/list/${encodeURIComponent(flag)}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function systemAlarmsPageQuery(id: number, home = '') {
  const q = new URLSearchParams()
  if (home) q.set('home', home)
  const suffix = q.toString() ? `?${q}` : ''
  return queryOptions({
    queryKey: ['device', id, 'system-alarms', home],
    queryFn: () => apiGet<DeviceSystemAlarmsPage>(`/devices/${id}/system-alarms${suffix}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export function systemAlarmsQuery(id: number, home: string) {
  return queryOptions({
    queryKey: ['device', id, 'system-alarms', home],
    queryFn: () => apiGet<DeviceSystemAlarmsPage>(`/devices/${id}/system-alarms?home=${encodeURIComponent(home)}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function systemPerformanceLiveQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'system-performance', 'live'],
    queryFn: () => apiGet<ProactiviteeLivePayload>(`/devices/${id}/system-performance/live`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: 1000,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function systemPerformanceQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'system-performance'],
    queryFn: () => apiGet<DeviceSystemPerformancePage>(`/devices/${id}/system-performance`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-fleet/v4/web/src/api/devices.ts */
export function systemPerformanceWatch(id: number) {
  return apiPost<{ success: boolean; ttl: number }>(`/devices/${id}/system-performance/watch`, {})
}

/* ------------------------------------------------------------------------- */
/* Fila Performance Management - /api/v2/devices/{id}/performance-management */
/* ------------------------------------------------------------------------- */


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function uploadDocs(id: number, body: FormData) {
  return apiPostForm<{ status: 'OK' }>(`/devices/${id}/documents`, body)
}


/* union from /tmp/v4-inventoree-writes/v4/web/src/api/devices.ts */
export function uploadQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'upload'],
    queryFn: () => apiGet<DeviceUploadPage>(`/devices/${id}/upload`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function wakeDevice(id: number) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/remote-ops/wake`, { device_unique: id })
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function watchPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'watch'],
    queryFn: () => apiGet<DeviceWatchPage>(`/devices/${id}/watch`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}


/* union from /tmp/v4-proac-leftover/v4/web/src/api/devices.ts */
export function watchServiceTemplate(id: number, service: number) {
  return apiGet<Array<{ field: string; value: string; placeholder?: string }>>(
    `/devices/${id}/watch/service-template?service=${service}`,
  )
}


/* union from /tmp/v4-opteemum-alarms/v4/web/src/api/devices.ts */
export function writeSystemServiceWatch(id: number, action: 'add' | 'remove', kind: string, name: string, port: number) {
  const path = action === 'remove' ? `/devices/${id}/system-alarms/service-watch/remove` : `/devices/${id}/system-alarms/service-watch`
  return apiPost<{ ok: boolean }>(path, { kind, name, port })
}



/* ---------------------------------------------------------------------------
 * Scrierile OS Management - /api/v2/devices/{id}/os-patches/install|uninstall|install-all
 * (Opteemum::installPatches / uninstallPatches, Remote_control::installOsUpdates).
 * `ticket` pune `it_comenzi.ticket_id` cand butonul e apasat din tichet.
 * ------------------------------------------------------------------------- */

export interface OsPatchWriteResult {
  ok: boolean
  sent: number
  message?: string
}

export function installOsPatches(id: number, patches: string[], ticket = 0) {
  return apiPost<OsPatchWriteResult>(`/devices/${id}/os-patches/install`, { patches, ticket })
}

export function uninstallOsPatches(id: number, patches: string[], ticket = 0) {
  return apiPost<OsPatchWriteResult>(`/devices/${id}/os-patches/uninstall`, { patches, ticket })
}

export function installAllOsPatches(id: number, ticket = 0, search = '') {
  return apiPost<OsPatchWriteResult>(`/devices/${id}/os-patches/install-all`, { ticket, search })
}

/** Softsee Operating System — Opteemum::installOsUpdates (dreptul install_os_updates). */
export function installAllSoftseeOsPatches(id: number, ticket = 0, search = '') {
  return apiPost<OsPatchWriteResult>(`/devices/${id}/os-updates/install-all`, { ticket, search })
}

export function installSoftseeOsPatches(id: number, patches: string[], ticket = 0) {
  return apiPost<OsPatchWriteResult>(`/devices/${id}/os-updates/install`, { patches, ticket })
}


/* ---------------------------------------------------------------------------
 * Scrierile marunte de pe fisa Opteemum (DeviceOpsWritesController):
 * Auto Update OS, UAC, scoaterea din startup, arhiva Remote Event Viewer.
 * ------------------------------------------------------------------------- */

export function setOsAutoUpdate(id: number, on: boolean, ticket = 0) {
  return apiPost<{ ok: boolean; auto_update_on: boolean }>(`/devices/${id}/os-updates/auto-update`, {
    autoUpdate: on ? 'on' : 'off',
    ticket,
  })
}

export interface UacLevel {
  title: string
  items: string[]
  note: string
}

export interface DeviceUacPage {
  device: number
  uac: number
  supported: boolean
  levels: UacLevel[]
}

export function uacQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'uac'],
    queryFn: () => apiGet<DeviceUacPage>(`/devices/${id}/uac`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function changeUac(id: number, uac: number, ticket = 0) {
  return apiPost<{ ok: boolean; uac: number; message?: string }>(`/devices/${id}/uac`, { uac, ticket })
}

export function removeFromStartup(id: number, app: number, ticket = 0) {
  return apiPost<{ ok: boolean; removed: string[] }>(`/devices/${id}/startup/remove`, { app, ticket })
}

export function removeAppsFromStartup(id: number, apps: number[], ticket = 0) {
  return apiPost<{ ok: boolean; removed: string[] }>(`/devices/${id}/startup/remove-many`, { removeApps: apps, ticket })
}

export function archiveRemoteEvent(
  id: number,
  event: { category: string; log_id: string; title: string; message: string },
  undo: boolean,
) {
  return apiPost<{ status: 'OK' | 'ERROR'; archived: number | null; message: string | null }>(
    `/devices/${id}/remote-events/archive`,
    { ...event, undo: undo ? 1 : 0 },
  )
}
